// Pure helper functions extracted from ProgramBuilder.js (Phase 1 refactor).
// These functions do NOT depend on component state/hooks — they receive all
// data as parameters — so they live here to keep ProgramBuilder lean and to be
// independently testable/reusable.
import {
	formatRestDisplay,
	normalizeSmartRatioInput,
	normalizeSmartRestInput,
	parseSmartRestSeconds,
} from "./utils";
import { CONDITIONING_TYPES } from "../components/ConditioningTemplateEditor";
import { joinEmomItemLoads, splitEmomItemLoad } from "./emomLoadUtils";
import {
	formatEmomStructure,
	getEmomStructureParts,
} from "./emomSummary";
import {
	getAutoReleaseDate,
	isAutoPublishEnabled,
} from "./programVisibility";
import { findElement, groupTargets } from "../components/shooting/stageElements";

export { getEmomRoundLabel, getEmomRoundAssignmentLabel } from "./emomRoundLabels";

/**
 * Cadence/duration summaries are built in pure helpers that have no React
 * context. Callers that render pass `t`; the Italian wording stays as the
 * fallback so an un-migrated call site keeps its current output instead of
 * printing a raw key.
 */
const MINUTE_WORDS = {
	every: { key: "pbEvery", it: "ogni" },
	minute: { key: "pbMinuteOne", it: "minuto" },
	minutes: { key: "pbMinuteMany", it: "minuti" },
};

// Compact prescription tokens. Same contract as `minuteWord`: `t` when
// rendering, Italian fallback for any call site still without it.
const RX_WORDS = {
	pbRxRec: "rec",
	pbRxSegments: "segmenti",
	pbRxSegFast: "veloce",
	pbRxSegMedium: "medio",
	pbRxSegSlow: "lento",
	pbRxCap: "cap",
	pbRxRound: "round",
	pbRxCycles: "cicli",
	pbRxWork: "work",
	pbRxReps: "rip",
	pbRxSeries: "serie",
	pbRxShots: "colpi",
	pbRxShotsPerSeries: "colpi/serie",
	pbRxFree: "libero",
	pbRxHandgun: "pistola",
	pbRxRifle: "carabina",
	pbRxShotgun: "fucile",
	pbRxPcc: "PCC",
};

const rx = (key, t) => (typeof t === "function" ? t(key) : RX_WORDS[key]);

const minuteWord = (which, t) => {
	const entry = MINUTE_WORDS[which];
	return typeof t === "function" ? t(entry.key) : entry.it;
};

export function formatConditioningCadence(intervalSeconds, t) {
	const secondsValue = Number(intervalSeconds);
	if (!Number.isFinite(secondsValue) || secondsValue <= 0) {
		return "";
	}

	const minutes = Math.floor(secondsValue / 60);
	const remainingSeconds = secondsValue % 60;

	if (remainingSeconds === 0) {
		return `${minuteWord("every", t)} ${minutes} ${minuteWord(
			minutes === 1 ? "minute" : "minutes",
			t,
		)}`;
	}

	if (minutes === 0) {
		return `${minuteWord("every", t)} ${remainingSeconds}s`;
	}

	return `${minuteWord("every", t)} ${minutes}:${remainingSeconds
		.toString()
		.padStart(2, "0")}`;
}

export function formatConditioningDuration(intervalSeconds, t) {
	const secondsValue = Number(intervalSeconds);
	if (!Number.isFinite(secondsValue) || secondsValue <= 0) {
		return "";
	}

	const minutes = Math.floor(secondsValue / 60);
	const remainingSeconds = secondsValue % 60;

	if (remainingSeconds === 0) {
		return `${minutes} ${minuteWord(
			minutes === 1 ? "minute" : "minutes",
			t,
		)}`;
	}

	if (minutes === 0) {
		return `${remainingSeconds}s`;
	}

	return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
}

export function formatHiitIntervalShort(intervalSeconds) {
	const secondsValue = Number(intervalSeconds);
	if (!Number.isFinite(secondsValue) || secondsValue <= 0) {
		return "";
	}

	const minutes = Math.floor(secondsValue / 60);
	const remainingSeconds = secondsValue % 60;

	if (remainingSeconds === 0) {
		return `${minutes}'`;
	}

	if (minutes === 0) {
		return `${remainingSeconds}\"`;
	}

	return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
}

export function parseHiitWorkRestRatio(ratioValue) {
	const ratioText = String(ratioValue || "").trim();
	if (!ratioText) return null;
	const match = ratioText.match(/^(\d+(?:\.\d+)?)\s*:\s*(\d+(?:\.\d+)?)$/);
	if (!match) return null;

	const work = Number(match[1]);
	const rest = Number(match[2]);
	if (
		!Number.isFinite(work) ||
		!Number.isFinite(rest) ||
		work <= 0 ||
		rest <= 0
	) {
		return null;
	}

	return { work, rest };
}

export function parseDurationStringToSeconds(rawValue) {
	const parsed = parseSmartRestSeconds(rawValue);
	if (!Number.isFinite(parsed) || parsed <= 0) return null;
	return Math.round(parsed);
}

export function getHiitWorkSecondsPerRound(exercise) {
	const numericSeconds = Number(exercise?.hiit_work_seconds);
	if (Number.isFinite(numericSeconds) && numericSeconds > 0) {
		return Math.round(numericSeconds);
	}

	const parsedSecondsFromSecondsField = parseDurationStringToSeconds(
		exercise?.hiit_work_seconds,
	);
	if (
		Number.isFinite(parsedSecondsFromSecondsField) &&
		parsedSecondsFromSecondsField > 0
	) {
		return parsedSecondsFromSecondsField;
	}

	return parseDurationStringToSeconds(exercise?.hiit_work_interval);
}

export function getHiitRestSecondsPerRound(exercise) {
	const numericSeconds = Number(exercise?.hiit_rest_seconds);
	if (Number.isFinite(numericSeconds) && numericSeconds > 0) {
		return Math.round(numericSeconds);
	}

	const parsedSecondsFromSecondsField = parseDurationStringToSeconds(
		exercise?.hiit_rest_seconds,
	);
	if (
		Number.isFinite(parsedSecondsFromSecondsField) &&
		parsedSecondsFromSecondsField > 0
	) {
		return parsedSecondsFromSecondsField;
	}

	return parseDurationStringToSeconds(exercise?.hiit_rest_interval);
}

export function getHiitOffLabel(exercise) {
	if (exercise?.hiit_rest_mode === "ratio") {
		const ratioText = normalizeSmartRatioInput(
			String(exercise?.hiit_rest_ratio || ""),
		);
		return ratioText ? `${ratioText} OFF` : "";
	}

	const restSeconds = getHiitRestSecondsPerRound(exercise);
	return restSeconds ? `${formatHiitIntervalShort(restSeconds)} OFF` : "";
}

export function getForTimeRoundsDisplay(roundsValue) {
	const rawValue =
		roundsValue === null || roundsValue === undefined
			? ""
			: String(roundsValue).trim();

	if (!rawValue) return "";
	if (/^\d+$/.test(rawValue)) {
		const roundsNumber = Number(rawValue);
		if (!Number.isFinite(roundsNumber) || roundsNumber <= 1) return "";
		return `${roundsNumber} rounds`;
	}

	return rawValue;
}

export function getRoundUnitLabel(roundsValue) {
	const rawValue =
		roundsValue === null || roundsValue === undefined
			? ""
			: String(roundsValue).trim();

	if (!rawValue) return "rounds";

	if (rawValue.includes("/")) {
		const values = rawValue
			.split("/")
			.map((part) => Number(String(part || "").trim()))
			.filter((value) => Number.isFinite(value));

		if (values.length === 2 && values[0] === 1 && values[1] === 1) {
			return "round";
		}

		return "rounds";
	}

	const numericValue = Number(rawValue);
	if (Number.isFinite(numericValue) && numericValue === 1) {
		return "round";
	}

	return "rounds";
}

// Format a Date object as local YYYY-MM-DD without timezone shifts
export const toLocalISODate = (date) => {
	const d = new Date(date);
	const yyyy = d.getFullYear();
	const mm = String(d.getMonth() + 1).padStart(2, "0");
	const dd = String(d.getDate()).padStart(2, "0");
	return `${yyyy}-${mm}-${dd}`;
};

export const parseLocalDate = (isoDateStr) => {
	if (!isoDateStr) return new Date();
	const [y, m, d] = isoDateStr.split("-").map(Number);
	return new Date(y, m - 1, d);
};

export const createRuntimeId = (prefix) =>
	`${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

export const getWeekMondayFromDate = (isoDate) => {
	if (!isoDate) return null;
	const date = parseLocalDate(isoDate);
	const monday = new Date(date);
	monday.setDate(date.getDate() - ((date.getDay() + 6) % 7));
	monday.setHours(0, 0, 0, 0);
	return monday;
};

export const getLatestDateFromWeeks = (weeks) => {
	const dates = (weeks || [])
		.flatMap((week) => (week.days || []).map((day) => day?.date))
		.filter(Boolean);
	if (dates.length === 0) return null;
	return dates.reduce((latest, current) =>
		current > latest ? current : latest,
	);
};

export const inferWeekdayOffset = (day, fallbackIndex = 0) => {
	if (day?.date) {
		const d = parseLocalDate(day.date);
		return (d.getDay() + 6) % 7;
	}
	if (typeof day?.day_number === "number" && day.day_number > 0) {
		return Math.min(6, day.day_number - 1);
	}
	return Math.max(0, Math.min(6, fallbackIndex));
};

export const getWeekAnchorMonday = (weeks, targetWeekIndex) => {
	const targetWeek = weeks?.[targetWeekIndex];
	const targetDates = (targetWeek?.days || [])
		.map((day) => day?.date)
		.filter(Boolean)
		.sort();
	if (targetDates.length > 0) {
		return getWeekMondayFromDate(targetDates[0]);
	}

	for (let idx = targetWeekIndex - 1; idx >= 0; idx -= 1) {
		const dates = (weeks?.[idx]?.days || [])
			.map((day) => day?.date)
			.filter(Boolean)
			.sort();
		if (dates.length > 0) {
			const monday = getWeekMondayFromDate(dates[0]);
			const shifted = new Date(monday);
			shifted.setDate(shifted.getDate() + (targetWeekIndex - idx) * 7);
			return shifted;
		}
	}

	for (let idx = targetWeekIndex + 1; idx < (weeks || []).length; idx += 1) {
		const dates = (weeks?.[idx]?.days || [])
			.map((day) => day?.date)
			.filter(Boolean)
			.sort();
		if (dates.length > 0) {
			const monday = getWeekMondayFromDate(dates[0]);
			const shifted = new Date(monday);
			shifted.setDate(shifted.getDate() - (idx - targetWeekIndex) * 7);
			return shifted;
		}
	}

	// Ultima riserva: lunedi' prossimo. Era un default muto («il programma si
	// prepara per la settimana dopo») e un coach che lavora «oggi per oggi»
	// si e' trovato le giornate nel futuro: ora la prima data di un programma
	// la sceglie il coach (`resolveFirstDayDate`, la domanda nel builder) e
	// qui si arriva solo per le settimane successive senza date.
	return parseLocalDate(getNextMondayISO());
};

// ---------------------------------------------------------------------------
// Da quando parte un programma datato
// (docs/program-builder-publishing/START_DATE.md)
// ---------------------------------------------------------------------------

export const FIRST_DAY_CHOICES = ["today", "next_monday", "custom"];

/** Il lunedi' della settimana dopo quella di `todayIso` (di lunedi': fra sette giorni). */
export const getNextMondayISO = (todayIso = toLocalISODate(new Date())) => {
	const date = parseLocalDate(todayIso);
	date.setDate(date.getDate() - ((date.getDay() + 6) % 7) + 7);
	return toLocalISODate(date);
};

/** Vero appena una giornata, in qualunque settimana, ha una data. */
export const programHasDatedDay = (programData) =>
	(programData?.weeks || []).some((week) =>
		(week?.days || []).some((day) => Boolean(day?.date)),
	);

/**
 * La data della prima giornata del programma dalla scelta del coach:
 * «Oggi», «Lunedi' prossimo» o una data sua. Senza data scelta per
 * «Altra data» non c'e' ancora un'ancora (null).
 */
export const resolveFirstDayDate = (
	choice,
	customIso = null,
	todayIso = toLocalISODate(new Date()),
) => {
	if (choice === "today") return todayIso;
	if (choice === "custom") return customIso || null;
	return getNextMondayISO(todayIso);
};

/** La prima giornata della settimana `weekIndex` (0 = settimana 1): sette giorni per settimana. */
export const firstDayDateForWeek = (firstDayIso, weekIndex = 0) => {
	const date = parseLocalDate(firstDayIso);
	date.setDate(date.getDate() + 7 * Math.max(0, Number(weekIndex) || 0));
	return toLocalISODate(date);
};

export const getComputedWeekDisableDate = (programData, weekIndex) => {
	const week = programData?.weeks?.[weekIndex];
	if (!week) return null;
	if (programData?.auto_disable_weeks === false) return null;
	if (week.keep_visible === true) return null;

	if (programData?.is_dated) {
		// Reads day_dates too, so an unloaded week is not mistaken for a week
		// without dates (which would show it as visible to athletes).
		const dates = getWeekDayDates(week).slice().sort();
		if (dates.length === 0) return null;

		const lastDayDate = parseLocalDate(dates[dates.length - 1]);
		const weekMonday = getWeekMondayFromDate(toLocalISODate(lastDayDate));
		if (!weekMonday) return null;

		const disableDate = new Date(weekMonday);
		// Week spans Monday-Sunday: default auto-disable is Sunday end-of-week.
		disableDate.setDate(disableDate.getDate() + 6);
		return toLocalISODate(disableDate);
	}

	const weeks = programData?.weeks || [];
	const inferredStarts = [];

	weeks.forEach((candidateWeek) => {
		if (!candidateWeek?.publish_date) return;
		const publishMonday = getWeekMondayFromDate(candidateWeek.publish_date);
		if (!publishMonday) return;

		const weekNumber = Number(candidateWeek?.week_number || 1);
		const inferredWeekOneStart = new Date(publishMonday);
		inferredWeekOneStart.setDate(
			inferredWeekOneStart.getDate() - (weekNumber - 1) * 7,
		);
		inferredStarts.push(inferredWeekOneStart);
	});

	let anchorMonday = null;
	if (inferredStarts.length > 0) {
		anchorMonday = [...inferredStarts].sort((a, b) => a - b)[0];
	} else if (programData?.created_at) {
		const createdAt = new Date(programData.created_at);
		if (!Number.isNaN(createdAt.getTime())) {
			anchorMonday = new Date(createdAt);
			anchorMonday.setHours(0, 0, 0, 0);
			anchorMonday.setDate(
				anchorMonday.getDate() - ((anchorMonday.getDay() + 6) % 7),
			);
		}
	}

	if (!anchorMonday) return null;

	const weekNumber = Number(week?.week_number || weekIndex + 1);
	const disableDate = new Date(anchorMonday);
	// Week N ends on Sunday of that week (anchor Monday + N*7 - 1 days).
	disableDate.setDate(disableDate.getDate() + weekNumber * 7 - 1);
	return toLocalISODate(disableDate);
};

/**
 * What athletes see of a week, for the editor's status button.
 *
 * `isDisabled` is a week released to athletes (published, or its publish date
 * reached) that its disable date now hides: the state a click reactivates.
 * The dates follow `isWeekPastForAthlete`: a manual date hides the week from
 * that day, the automatic one (the Sunday closing the week) from the day after.
 */
export const getWeekVisibilityForAthlete = (
	programData,
	week,
	weekIndex,
	todayIso = toLocalISODate(new Date()),
) => {
	if (!week) {
		return {
			isVisible: false,
			isDisabled: false,
			isManualDisable: false,
			effectiveDisableDate: null,
			autoReleaseDate: null,
			releaseReason: null,
		};
	}

	const manualDisableDate = week.disable_date || null;
	const effectiveDisableDate =
		manualDisableDate || getComputedWeekDisableDate(programData, weekIndex);
	const isExpired = manualDisableDate
		? manualDisableDate <= todayIso
		: Boolean(effectiveDisableDate) && effectiveDisableDate < todayIso;
	// Released by hand, by its date, or by the program's automatic release
	// (`auto_publish_weeks`: from the Monday of its first day minus the lead,
	// unless held back). Same rule as `isWeekVisibleForAthlete`.
	const autoReleaseDate = getAutoReleaseDate(programData, week);
	const releaseReason =
		week.is_published === true
			? "manual"
			: week.publish_date && week.publish_date <= todayIso
				? "date"
				: week.hold !== true && autoReleaseDate && autoReleaseDate <= todayIso
					? "auto"
					: null;
	const isReleased = releaseReason !== null;

	return {
		isVisible: isReleased && !isExpired,
		isDisabled: isReleased && isExpired,
		isManualDisable: Boolean(manualDisableDate),
		effectiveDisableDate,
		autoReleaseDate,
		releaseReason,
	};
};

const shiftISODay = (isoDate, days) => {
	const parsed = parseLocalDate(isoDate);
	if (!parsed || Number.isNaN(parsed.getTime())) return null;
	parsed.setDate(parsed.getDate() + days);
	return toLocalISODate(parsed);
};

/** The ISO day after `isoDate` (null for an invalid date). */
export const nextISODay = (isoDate) => shiftISODay(isoDate, 1);

/**
 * What athletes see of a week, as one object the status line, the dot in the
 * week strip and the save toast all read — so the same week is described with
 * the same words everywhere instead of being recomposed from four buttons.
 *
 * `state`: "draft" | "scheduled" | "live" | "expired".
 * `from`: the publish date behind a scheduled week, or behind a visible week
 *   released by its date (null when the coach published it by hand: there is
 *   no date to show).
 * `hiddenFrom`: the first day athletes no longer see it. A manual date hides
 *   from that day, the automatic one (the Sunday closing the week) from the day
 *   after. Null when nothing hides it.
 * `visibleUntil`: the last day athletes see it, the day before `hiddenFrom`.
 *   It is what the builder shows and what its calendar picks: coaches think
 *   «visible through Sunday», not «hidden from Monday». The stored
 *   `disable_date` stays the first hidden day.
 * `hideReason`: "manual" | "auto" | "keeps" (reactivated by hand, or automatic
 *   expiry off for the whole program) | null (no date either way).
 * `previewCount`: athletes who see it although it is not released.
 * `conflict`: the dates together mean athletes never see the week on its own
 *   training days, and nothing else says so. "hides_before_publish": hidden
 *   on or before the day it publishes. "hides_before_first_day": hidden on or
 *   before its first day (dated programs), e.g. visible 1-7 Nov for a week of
 *   workouts on 9-13 Nov. "auto_release_empty": the automatic release will
 *   publish a week whose days hold no exercise yet.
 * `firstDay`: the week's first dated day, for the conflict sentence.
 * `autoPublish`: the program releases weeks by itself (`auto_publish_weeks`).
 * `autoReleaseDate`: when this week releases by itself (null: never, e.g. no
 *   dated days). `releaseReason`: "manual" | "date" | "auto" | null, why it
 *   is (or will be) released; for a scheduled week, which of the two dates
 *   comes first. `held`: the coach holds it back from the automatic release.
 */
export const describeWeekVisibility = (
	programData,
	week,
	weekIndex,
	todayIso = toLocalISODate(new Date()),
) => {
	const visibility = getWeekVisibilityForAthlete(
		programData,
		week,
		weekIndex,
		todayIso,
	);
	const autoPublish = isAutoPublishEnabled(programData);
	const base = {
		...visibility,
		state: "draft",
		from: null,
		hiddenFrom: null,
		visibleUntil: null,
		hideReason: null,
		previewCount: 0,
		conflict: null,
		autoPublish,
		held: false,
	};
	if (!week) return base;

	const manualDisableDate = week.disable_date || null;
	let hiddenFrom = null;
	let hideReason = null;
	if (manualDisableDate) {
		hiddenFrom = manualDisableDate;
		hideReason = "manual";
	} else if (visibility.effectiveDisableDate) {
		hiddenFrom = nextISODay(visibility.effectiveDisableDate);
		hideReason = "auto";
	} else if (
		programData?.auto_disable_weeks === false ||
		week.keep_visible === true
	) {
		hideReason = "keeps";
	}

	// A held week is a draft the calendar will not touch; the coach releases it.
	const held =
		autoPublish && week.hold === true && !visibility.isVisible && !visibility.isDisabled;
	const scheduledByDate =
		week.publish_date && week.publish_date > todayIso ? week.publish_date : null;
	const scheduledByCalendar =
		!held && visibility.autoReleaseDate && visibility.autoReleaseDate > todayIso
			? visibility.autoReleaseDate
			: null;

	let state = "draft";
	let releaseReason = visibility.releaseReason;
	let from = null;
	if (visibility.isVisible) {
		state = "live";
		// A date to show only when a date released it: by hand there is none.
		from =
			releaseReason === "date"
				? week.publish_date
				: releaseReason === "auto"
					? visibility.autoReleaseDate
					: null;
	} else if (visibility.isDisabled) {
		state = "expired";
	} else if (scheduledByDate || scheduledByCalendar) {
		state = "scheduled";
		// Whichever comes first is the day athletes will see it.
		if (scheduledByDate && (!scheduledByCalendar || scheduledByDate <= scheduledByCalendar)) {
			from = scheduledByDate;
			releaseReason = "date";
		} else {
			from = scheduledByCalendar;
			releaseReason = "auto";
		}
	}

	const previewCount =
		state === "live"
			? 0
			: (Array.isArray(week.preview_athlete_ids)
					? week.preview_athlete_ids
					: []
				).length;

	const firstDay =
		programData?.is_dated !== false
			? getWeekDayDates(week).slice().sort()[0] || null
			: null;
	let conflict = null;
	if (hiddenFrom && (state === "live" || state === "scheduled")) {
		if (from && hiddenFrom <= from) {
			conflict = "hides_before_publish";
		} else if (firstDay && hiddenFrom <= firstDay) {
			conflict = "hides_before_first_day";
		}
	}
	// The calendar is about to publish a week nobody has written yet. Only on
	// loaded days: a week narrowed to its dates (`weeks_window`) has no days.
	if (!conflict && state === "scheduled" && releaseReason === "auto") {
		const loadedDays = Array.isArray(week.days) ? week.days : [];
		if (
			loadedDays.length > 0 &&
			loadedDays.every((day) => getNormalizedDayExercises(day).length === 0)
		) {
			conflict = "auto_release_empty";
		}
	}

	return {
		...base,
		state,
		from,
		hiddenFrom,
		visibleUntil: hiddenFrom ? shiftISODay(hiddenFrom, -1) : null,
		hideReason,
		previewCount,
		conflict,
		firstDay,
		releaseReason,
		held,
	};
};

/**
 * The status sentence for a `describeWeekVisibility` result. `formatDay`
 * renders an ISO date the way the caller wants it (short, with the weekday);
 * `athleteCount` is how many athletes the program reaches; `pendingSave` says
 * the described state is not saved yet, so the sentence does not claim that
 * athletes already see something they do not.
 */
export const formatWeekVisibilityLabel = (
	description,
	{ t, formatDay, athleteCount = null, pendingSave = false } = {},
) => {
	if (!description || typeof t !== "function") return "";
	const day = (iso) => (typeof formatDay === "function" ? formatDay(iso) : iso);
	const athletesWord = (count) =>
		count === 1 ? t("athlete").toLowerCase() : t("athletes").toLowerCase();
	const reasonWord = (reason) =>
		reason === "manual" ? t("pbWeekHideManual") : t("pbWeekHideAuto");
	const parts = [];

	// "(automatico)" after a date the calendar chose, not the coach.
	const autoSuffix = description.releaseReason === "auto" ? ` (${t("pbWeekHideAuto")})` : "";

	switch (description.state) {
		case "live":
			parts.push(
				description.from
					? `${t("pbWeekStatusVisibleSince")} ${day(description.from)}${autoSuffix}`
					: t("pbWeekStatusVisible"),
			);
			break;
		case "scheduled":
			parts.push(`${t("pbWeekStatusScheduled")} ${day(description.from)}${autoSuffix}`);
			break;
		case "expired":
			parts.push(
				`${t("pbWeekStatusHidden")} ${day(description.hiddenFrom)} (${reasonWord(
					description.hideReason,
				)})`,
			);
			break;
		default:
			if (description.held) {
				// Held back from the automatic release: say so, and when the
				// workouts start, so the coach knows what the hold is costing.
				parts.push(
					description.firstDay
						? `${t("pbWeekHeld")} · ${t("pbWeekHeldStarts")} ${day(description.firstDay)}`
						: t("pbWeekHeld"),
				);
			} else if (description.autoPublish && !description.autoReleaseDate) {
				parts.push(`${t("pbWeekStatusDraft")} · ${t("pbWeekNoDatedDays")}`);
			} else {
				parts.push(t("pbWeekStatusDraft"));
			}
	}

	if (description.state === "live" || description.state === "scheduled") {
		if (description.visibleUntil) {
			parts.push(
				`${t("pbWeekVisibleUntil")} ${day(description.visibleUntil)} (${reasonWord(
					description.hideReason,
				)})`,
			);
		} else if (description.hideReason === "keeps") {
			parts.push(t("pbWeekStaysVisible"));
		}
	}

	if (description.previewCount > 0) {
		parts.push(
			`${t("pbWeekPreviewFor")} ${description.previewCount} ${athletesWord(
				description.previewCount,
			)}`,
		);
	}

	// Concrete dates, not an abstract warning: the coach has to see which two
	// dates collide to know which one to move.
	if (description.conflict === "hides_before_first_day") {
		parts.push(
			`${t("pbWeekConflictFirstDay")} ${day(description.firstDay)}, ${t(
				"pbWeekConflictButHidden",
			)} ${day(description.visibleUntil)}`,
		);
	} else if (description.conflict === "hides_before_publish") {
		parts.push(
			`${t("pbWeekConflictPublish")} ${day(description.from)}, ${t(
				"pbWeekConflictButHidden",
			)} ${day(description.visibleUntil)}`,
		);
	} else if (description.conflict === "auto_release_empty") {
		parts.push(
			`${t("pbWeekConflictPublish")} ${day(description.from)} ${t("pbWeekConflictEmpty")}`,
		);
	}

	if (typeof athleteCount === "number") {
		if (athleteCount === 0) {
			parts.push(t("pbWeekNoAthletes"));
		} else if (description.state === "live") {
			// "1 atleta la vede", "12 atleti la vedono".
			parts.push(
				`${athleteCount} ${athletesWord(athleteCount)} ${t(
					athleteCount === 1 ? "pbWeekSeenByOne" : "pbWeekSeenBy",
				)}`,
			);
		}
	}

	if (pendingSave) parts.push(t("pbWeekAfterSave"));
	return parts.join(" · ");
};

/**
 * One click on the week status button: a week athletes see gets hidden, any
 * other one (draft, or disabled by its date) becomes visible now. Showing a
 * week its disable date had hidden drops that date, which has served its
 * purpose, and if the automatic expiry would hide it again marks it
 * `keep_visible`, so it stays until the coach hides it or picks a new date.
 * In a program that releases weeks by itself, hiding also holds the week
 * (`hold`), or the calendar would show it again at once; showing clears a
 * hold. Returns the new week.
 */
export const toggleWeekVisibility = (
	programData,
	weekIndex,
	todayIso = toLocalISODate(new Date()),
) => {
	const weeks = programData?.weeks || [];
	const week = weeks[weekIndex];
	if (!week) return week;

	if (
		getWeekVisibilityForAthlete(programData, week, weekIndex, todayIso)
			.isVisible
	) {
		return {
			...week,
			is_published: false,
			keep_visible: false,
			publish_date:
				week.publish_date && week.publish_date <= todayIso
					? null
					: week.publish_date || null,
			// Only in an automatic program: a manual one never gets the key.
			...(isAutoPublishEnabled(programData) ? { hold: true } : {}),
		};
	}

	const shown = {
		...week,
		is_published: true,
		publish_date: null,
		keep_visible: false,
		disable_date:
			week.disable_date && week.disable_date <= todayIso
				? null
				: week.disable_date || null,
		...(week.hold ? { hold: false } : {}),
	};
	const withShown = {
		...programData,
		weeks: weeks.map((w, i) => (i === weekIndex ? shown : w)),
	};
	return getWeekVisibilityForAthlete(withShown, shown, weekIndex, todayIso)
		.isVisible
		? shown
		: { ...shown, keep_visible: true };
};

/**
 * Hold a week back from the program's automatic release («Trattieni»), or
 * release it («Rilascia»). Holding also drops a scheduled publish date: held
 * means «it does not publish until I release it», whatever the date said.
 */
export const setWeekHold = (week, held) => {
	if (!week) return week;
	if (held) {
		return { ...week, hold: true, is_published: false, publish_date: null };
	}
	return { ...week, hold: false };
};

/**
 * What switching the automatic release on would change for athletes: the
 * weeks they see (or are scheduled to see) before their automatic day. Those
 * weeks go back to drafts the calendar will publish on `releaseDate`. Past and
 * current weeks are not touched: the calendar would show them anyway.
 */
export const autoPublishActivationPlan = (
	programData,
	todayIso = toLocalISODate(new Date()),
) => {
	const candidate = {
		...programData,
		auto_publish_weeks: true,
		auto_publish_lead_days:
			programData?.auto_publish_lead_days === undefined ||
			programData?.auto_publish_lead_days === null
				? 1
				: programData.auto_publish_lead_days,
	};
	const weeks = [];
	(programData?.weeks || []).forEach((week, index) => {
		if (!week) return;
		const releaseDate = getAutoReleaseDate(candidate, week);
		if (!releaseDate || releaseDate <= todayIso) return;
		const releasedEarly =
			week.is_published === true ||
			Boolean(week.publish_date && week.publish_date < releaseDate);
		if (!releasedEarly) return;
		weeks.push({
			index,
			week,
			releaseDate,
			visibleNow: getWeekVisibilityForAthlete(programData, week, index, todayIso)
				.isVisible,
		});
	});
	return { weeks, firstRelease: weeks[0]?.releaseDate || null };
};

/**
 * Switch the automatic release on or off. On: the weeks of the activation
 * plan become drafts for the calendar; nothing else moves. Off: only the
 * flag changes — weeks already published stay published, future ones become
 * plain drafts to publish by hand. Switching off never takes anything away.
 */
export const applyAutoPublish = (
	programData,
	enabled,
	todayIso = toLocalISODate(new Date()),
) => {
	if (!programData) return programData;
	if (!enabled) {
		return { ...programData, auto_publish_weeks: false };
	}
	const plan = autoPublishActivationPlan(programData, todayIso);
	const reset = new Set(plan.weeks.map((entry) => entry.index));
	return {
		...programData,
		auto_publish_weeks: true,
		auto_publish_lead_days:
			programData.auto_publish_lead_days === undefined ||
			programData.auto_publish_lead_days === null
				? 1
				: programData.auto_publish_lead_days,
		weeks: (programData.weeks || []).map((week, index) =>
			reset.has(index)
				? { ...week, is_published: false, publish_date: null, keep_visible: false }
				: week,
		),
	};
};

export const getNormalizedDayExercises = (day) => {
	const sessions = day?.sessions || [];
	let exercises = (day?.exercises || []).map((exercise, idx) => ({
		...exercise,
		order: typeof exercise.order === "number" ? exercise.order : idx,
	}));

	if (sessions.length > 0) {
		const flattenedSessionExercises = sessions.flatMap((session) =>
			(session?.exercises || []).map((exercise, exIdx) => ({
				...exercise,
				session_id: session?.session_id || exercise?.session_id || null,
				session_order:
					typeof exercise?.session_order === "number"
						? exercise.session_order
						: exIdx,
				order: typeof exercise?.order === "number" ? exercise.order : exIdx,
			})),
		);

		if (flattenedSessionExercises.length > 0) {
			// Il mirror delle sedute è la fonte, ma NON è tutto il giorno: un
			// esercizio senza `session_id` nel mirror non c'è, e prendendo solo
			// quello spariva dalla panoramica della settimana e dal pannello di
			// riferimento. Succede ogni volta che si aggiunge una seduta a un
			// giorno che aveva già degli esercizi — dall'editor, dove la riga
			// resta visibile e quindi nessuno si accorge che altrove non c'è.
			// Mostrarlo senza etichetta di seduta è peggio che perfetto e molto
			// meglio che perderlo.
			const sessionIds = new Set(
				sessions.map((session) => session?.session_id).filter(Boolean),
			);
			const orphans = exercises.filter(
				(exercise) => !sessionIds.has(exercise?.session_id),
			);
			exercises = [...flattenedSessionExercises, ...orphans];
		}
	}

	return [...exercises].sort(
		(a, b) => Number(a?.order || 0) - Number(b?.order || 0),
	);
};

/** A load written as a percentage of a max ("75", "75%", "70-75"), not "110kg". */
export const hasPercentageLikeLoad = (value) => {
	if (value === null || value === undefined) return false;
	const raw = String(value).trim();
	if (!raw) return false;
	if (raw.includes("%")) return true;
	if (/[a-zA-Z]/.test(raw)) return false;
	return /^\d+(?:[\.,]\d+)?(?:[\/-]\d+(?:[\.,]\d+)?)?$/.test(raw);
};

const normalizeReferenceName = (value) =>
	String(value || "")
		.trim()
		.toLowerCase()
		.split(/\s+/)
		.join(" ");

export const isStrengthComplex = (exercise) =>
	exercise?.methodology === "complex";

/**
 * A complex has no max of its own: a percentage on a complex row is a
 * percentage of ONE movement, and the coach has to say which. A reference
 * pointing at the complex itself — its own id, or its generated "A + B + C"
 * name, which is what copying a day used to write into the rows — is the
 * same as no reference.
 */
const isComplexSelfReference = (exercise, referenceId, referenceName) => {
	if (referenceId && referenceId === exercise?.exercise_id) return true;
	const name = normalizeReferenceName(referenceName);
	if (!name) return false;
	const sequenceName = normalizeReferenceName(
		(exercise?.complex_exercises || [])
			.map((item) => item?.exercise_name)
			.filter(Boolean)
			.join(" + "),
	);
	return (
		name === normalizeReferenceName(exercise?.exercise_name) ||
		(Boolean(sequenceName) && name === sequenceName)
	);
};

/**
 * The reference the coach declared for a complex row ({ id, name }), or null.
 * Row first, then the exercise — the same order every reader uses.
 */
export const getDeclaredComplexReference = (exercise, row = null) => {
	const candidates = [
		row
			? {
					id: row.load_reference_exercise_id || null,
					name: row.load_reference_exercise_name || null,
				}
			: null,
		{
			id: exercise?.load_reference_exercise_id || null,
			name: exercise?.load_reference_exercise_name || null,
		},
	];
	for (const candidate of candidates) {
		if (!candidate || (!candidate.id && !candidate.name)) continue;
		if (isComplexSelfReference(exercise, candidate.id, candidate.name)) continue;
		const movement = candidate.name
			? null
			: (exercise?.complex_exercises || []).find(
					(item) => item?.exercise_id && item.exercise_id === candidate.id,
				);
		return {
			id: candidate.id,
			name: candidate.name || movement?.exercise_name || null,
		};
	}
	return null;
};

/** True when a complex has a percentage row that does not say on which max. */
export const complexMissingLoadReference = (exercise, rows = null) =>
	isStrengthComplex(exercise) &&
	(rows || exercise?.set_rows || []).some(
		(row) =>
			hasPercentageLikeLoad(row?.load_percentage) &&
			!getDeclaredComplexReference(exercise, row),
	);

export const resolveExerciseLoadReferencesForStats = (exerciseValue) => {
	if (!exerciseValue || typeof exerciseValue !== "object") return exerciseValue;

	const hasPercentageLikeValue = hasPercentageLikeLoad;

	const resolvedExercise = { ...exerciseValue };
	// A complex is never its own reference: copying it must not "declare" the
	// complex as the max to compute on (it would then look set in the builder).
	// Only a reference the coach chose travels with the copy.
	const complexReference = isStrengthComplex(resolvedExercise)
		? getDeclaredComplexReference(resolvedExercise)
		: null;
	const fallbackReferenceId = isStrengthComplex(resolvedExercise)
		? complexReference?.id || null
		: resolvedExercise.load_reference_exercise_id ||
			resolvedExercise.exercise_id ||
			null;
	const fallbackReferenceName = isStrengthComplex(resolvedExercise)
		? complexReference?.name || null
		: resolvedExercise.load_reference_exercise_name ||
			resolvedExercise.exercise_name ||
			null;

	if (
		resolvedExercise.load_percentage &&
		!resolvedExercise.load_reference_exercise_id &&
		!resolvedExercise.load_reference_exercise_name
	) {
		resolvedExercise.load_reference_exercise_id = fallbackReferenceId;
		resolvedExercise.load_reference_exercise_name = fallbackReferenceName;
	}

	resolvedExercise.set_rows = (resolvedExercise.set_rows || []).map((row) => {
		if (!row || typeof row !== "object") return row;
		if (!row.load_percentage) return row;
		if (row.load_reference_exercise_id || row.load_reference_exercise_name) {
			return row;
		}
		return {
			...row,
			load_reference_exercise_id: fallbackReferenceId,
			load_reference_exercise_name: fallbackReferenceName,
		};
	});

	resolvedExercise.superset_exercises = (
		resolvedExercise.superset_exercises || []
	).map((supersetExercise) => {
		if (!supersetExercise || typeof supersetExercise !== "object") {
			return supersetExercise;
		}
		const supersetReferenceId =
			supersetExercise.load_reference_exercise_id ||
			supersetExercise.exercise_id ||
			fallbackReferenceId;
		const supersetReferenceName =
			supersetExercise.load_reference_exercise_name ||
			supersetExercise.exercise_name ||
			fallbackReferenceName;

		return {
			...supersetExercise,
			set_rows: (supersetExercise.set_rows || []).map((row) => {
				if (!row || typeof row !== "object") return row;
				if (!row.load_percentage) return row;
				if (
					row.load_reference_exercise_id ||
					row.load_reference_exercise_name
				) {
					return row;
				}
				return {
					...row,
					load_reference_exercise_id: supersetReferenceId,
					load_reference_exercise_name: supersetReferenceName,
				};
			}),
		};
	});

	resolvedExercise.emom_rounds = (resolvedExercise.emom_rounds || []).map(
		(round) => ({
			...round,
			items: (round?.items || []).map((item) => {
				if (!item || typeof item !== "object") return item;
				if (!hasPercentageLikeValue(item.load)) return item;
				if (
					item.load_reference_exercise_id ||
					item.load_reference_exercise_name
				) {
					return item;
				}
				return {
					...item,
					load_reference_exercise_id: item.exercise_id || fallbackReferenceId,
					load_reference_exercise_name:
						item.exercise_name || fallbackReferenceName,
				};
			}),
		}),
	);

	return resolvedExercise;
};

export const cloneExerciseAsIndependent = (
	exercise,
	{ orderValue = 0, sessionIdValue = null, sessionOrderValue = null } = {},
) => {
	const resolvedExercise = resolveExerciseLoadReferencesForStats(exercise);
	const originalExerciseId = resolvedExercise?.exercise_id || null;
	const originalSourceExerciseId =
		resolvedExercise?.source_exercise_id || originalExerciseId;

	return {
		...resolvedExercise,
		exercise_id: createRuntimeId("ex"),
		source_exercise_id: originalSourceExerciseId,
		order: orderValue,
		session_id: sessionIdValue,
		session_order: sessionOrderValue,
		shared_exercise_id: null,
		shared_origin_program_id: null,
		shared_origin_week_id: null,
		shared_origin_day_id: null,
		shared_origin_exercise_id: null,
		shared_is_origin: false,
		shared_readonly: false,
	};
};

/**
 * Marks every exercise of a day as the origin of a shared exercise (week share).
 *
 * Each row gets its own `shared_exercise_id` (kept when the row is already
 * shared). Rows are handled by position, never matched by `exercise_id`: two
 * rows of the same day can carry the same id, or none at all (circuits, rows
 * written by the AI tools), and matching by id collapsed them onto one payload.
 *
 * The `sessions[].exercises` mirror is rebuilt from the flat list, the same way
 * the save path does, so both representations carry the same shared ids.
 *
 * Mutates `day` in place and returns the origin payloads in display order; the
 * caller derives the read-only copies for the target program from them.
 */
export const markDayExercisesAsSharedOrigin = (
	day,
	{ programId, weekId = null } = {},
) => {
	if (!day || typeof day !== "object") return { day, originExercises: [] };

	const flatExercises = Array.isArray(day.exercises) ? day.exercises : [];
	const baseExercises =
		flatExercises.length > 0 ? flatExercises : getNormalizedDayExercises(day);

	const originExercises = baseExercises.map((exercise, idx) => ({
		...resolveExerciseLoadReferencesForStats(exercise),
		order: typeof exercise?.order === "number" ? exercise.order : idx,
		shared_exercise_id: exercise?.shared_exercise_id || createRuntimeId("shx"),
		shared_origin_program_id: programId,
		shared_origin_week_id: weekId || null,
		shared_origin_day_id: day.day_id || null,
		shared_origin_exercise_id:
			exercise?.shared_origin_exercise_id || exercise?.exercise_id || null,
		shared_is_origin: true,
		shared_readonly: false,
	}));

	day.exercises = originExercises;

	const sessions = Array.isArray(day.sessions) ? day.sessions : [];
	if (sessions.length > 0) {
		day.sessions = sessions.map((session) => ({
			...session,
			exercises: originExercises
				.filter(
					(exercise) =>
						exercise.session_id && exercise.session_id === session?.session_id,
				)
				.map((exercise, exIdx) => ({ ...exercise, session_order: exIdx })),
		}));
	}

	return {
		day,
		originExercises: [...originExercises].sort(
			(a, b) => Number(a.order || 0) - Number(b.order || 0),
		),
	};
};

export const cloneDayAsIndependent = (
	sourceDay,
	{
		dateValue = undefined,
		dayNumberValue = undefined,
		titleValue = undefined,
	} = {},
) => {
	const sessionIdMap = new Map();

	const clonedSessions = (sourceDay?.sessions || []).map(
		(session, sessionIdx) => {
			const newSessionId = createRuntimeId("sess");
			if (session?.session_id) {
				sessionIdMap.set(session.session_id, newSessionId);
			}

			return {
				...session,
				session_id: newSessionId,
				order: typeof session?.order === "number" ? session.order : sessionIdx,
				exercises: (session?.exercises || []).map((exercise, exIdx) =>
					cloneExerciseAsIndependent(exercise, {
						orderValue:
							typeof exercise?.order === "number" ? exercise.order : exIdx,
						sessionIdValue: newSessionId,
						sessionOrderValue:
							typeof exercise?.session_order === "number"
								? exercise.session_order
								: exIdx,
					}),
				),
			};
		},
	);

	const normalizedExercises = getNormalizedDayExercises(sourceDay).map(
		(exercise, exIdx) => {
			const mappedSessionId = sessionIdMap.get(exercise?.session_id);
			return cloneExerciseAsIndependent(exercise, {
				orderValue:
					typeof exercise?.order === "number" ? exercise.order : exIdx,
				sessionIdValue: mappedSessionId || null,
				sessionOrderValue: mappedSessionId
					? typeof exercise?.session_order === "number"
						? exercise.session_order
						: exIdx
					: null,
			});
		},
	);

	return {
		...sourceDay,
		day_id: createRuntimeId("day"),
		date: dateValue,
		day_number: dayNumberValue,
		title: titleValue !== undefined ? titleValue : sourceDay?.title,
		sessions: clonedSessions,
		exercises: normalizedExercises,
	};
};

// A plain export (week/day) must be a fully independent duplicate: strip all
// `shared_*` links and regenerate exercise ids, otherwise the destination keeps
// pointers to the live source program and the backend result-schema lock fires.
export const cloneDayForExport = (
	sourceDay,
	{ dateValue = undefined, dayNumberValue = undefined } = {},
) => cloneDayAsIndependent(sourceDay, { dateValue, dayNumberValue });

export const getCurrentCalendarWeekRange = () => {
	const now = new Date();
	now.setHours(0, 0, 0, 0);
	const monday = new Date(now);
	monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
	const sunday = new Date(monday);
	sunday.setDate(monday.getDate() + 6);
	return {
		start: toLocalISODate(monday),
		end: toLocalISODate(sunday),
	};
};

/**
 * Whether a week arrived with its day content.
 *
 * The editor can load a program with only its recent weeks populated
 * (`GET /programs/{id}?weeks_window=N`); the older ones come back as skeletons
 * carrying metadata and `day_dates` but deliberately no `days` key at all, so
 * there is no day content to send back and erase them with.
 */
export const isWeekContentLoaded = (week) =>
	!week?.days_omitted && Array.isArray(week?.days);

/**
 * Day dates of a week, whether it is loaded or still a skeleton.
 *
 * Week labelling, the "current week" lookup and athlete visibility all need the
 * dates but none of the exercises, which is exactly why skeletons keep them.
 */
export const getWeekDayDates = (week) => {
	if (Array.isArray(week?.days)) {
		return week.days.map((day) => day?.date).filter(Boolean);
	}
	if (Array.isArray(week?.day_dates)) {
		return week.day_dates.filter(Boolean);
	}
	return [];
};

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Give a date to every day of a loaded week that has none.
 *
 * Days created while a program is undated carry `date: null`. Switching the
 * program to dated used to keep them that way, and the builder rendered every
 * one of them as "1 January 1970". Dated days stay where they are; a dateless
 * day goes on the weekday its `day_number` suggests, never before the day that
 * precedes it, counting from the week's anchor Monday (the neighbouring weeks,
 * else next Monday). Weeks are filled in order so that each one anchors on the
 * previous. Skeleton weeks are left alone: their days are not in memory.
 *
 * Returns the same array when there is nothing to fill.
 */
export const fillMissingDayDates = (weeks) => {
	if (!Array.isArray(weeks)) return weeks;
	let changed = false;
	const result = [...weeks];

	result.forEach((week, weekIndex) => {
		if (!isWeekContentLoaded(week)) return;
		if (!week.days.some((day) => !day?.date)) return;

		const monday = getWeekAnchorMonday(result, weekIndex);
		let previousOffset = -1;
		const days = week.days.map((day, dayIndex) => {
			if (day?.date) {
				previousOffset = Math.round(
					(parseLocalDate(day.date) - monday) / DAY_MS,
				);
				return day;
			}
			const offset = Math.max(
				inferWeekdayOffset(day, dayIndex),
				previousOffset + 1,
			);
			previousOffset = offset;
			const date = new Date(monday);
			date.setDate(date.getDate() + offset);
			return { ...day, date: toLocalISODate(date) };
		});

		result[weekIndex] = { ...week, days };
		changed = true;
	});

	return changed ? result : weeks;
};

const shiftISODateString = (iso, days) => {
	const date = parseLocalDate(iso);
	date.setDate(date.getDate() + days);
	return toLocalISODate(date);
};

/**
 * Move every date a week carries by `days`, keeping its content untouched.
 *
 * Used when a week is removed from a dated program and the ones after it have
 * to close the hole: without this the coach has to retype the date of every
 * single day, and until they do the phases and races of the season agenda no
 * longer line up with the weeks.
 *
 * Only meaningful on a loaded week: a skeleton's `day_dates` never reach the
 * database (the save strips them and the backend restores the stored days), so
 * callers must fill skeletons in before shifting them.
 */
export const shiftWeekDates = (week, days) => {
	if (!week || !days) return week;
	const shifted = { ...week };
	if (Array.isArray(week.days)) {
		shifted.days = week.days.map((day) =>
			day?.date ? { ...day, date: shiftISODateString(day.date, days) } : day,
		);
	}
	if (Array.isArray(week.day_dates)) {
		shifted.day_dates = week.day_dates.map((date) =>
			date ? shiftISODateString(date, days) : date,
		);
	}
	if (week.publish_date) {
		shifted.publish_date = shiftISODateString(week.publish_date, days);
	}
	if (week.disable_date) {
		shifted.disable_date = shiftISODateString(week.disable_date, days);
	}
	return shifted;
};

/**
 * Normalize one week as the editor expects it: session/exercise ordering filled
 * in, exercises reconciled with their sessions.
 *
 * Shared by the initial program load and the on-demand load of an older week,
 * so both produce identical state. Skeleton weeks are returned untouched —
 * normalizing them would materialise a `days` array they must not have.
 */
export const normalizeProgramWeek = (week) => {
	if (!isWeekContentLoaded(week)) {
		return {
			...week,
			disable_date: week?.disable_date || null,
			preview_athlete_ids: week?.preview_athlete_ids || [],
		};
	}

	return {
		...week,
		disable_date: week.disable_date || null,
		preview_athlete_ids: week.preview_athlete_ids || [],
		days: (week.days || []).map((day) => {
			const sessions = (day.sessions || [])
				.map((session, index) => ({
					...session,
					order: typeof session.order === "number" ? session.order : index,
					exercises: (session.exercises || []).map((exercise, exIndex) => ({
						...exercise,
						order:
							typeof exercise.order === "number" ? exercise.order : exIndex,
						session_id: session.session_id,
					})),
				}))
				.sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
				.map((session, index) => ({ ...session, order: index }));

			let exercises = (day.exercises || []).map((exercise, exIndex) => ({
				...exercise,
				order: typeof exercise.order === "number" ? exercise.order : exIndex,
				session_id: exercise.session_id || null,
				session_order:
					typeof exercise.session_order === "number"
						? exercise.session_order
						: null,
			}));

			if (
				exercises.length === 0 &&
				sessions.some((session) => (session.exercises || []).length > 0)
			) {
				exercises = sessions.flatMap((session) =>
					(session.exercises || []).map((exercise) => ({
						...exercise,
						session_id: session.session_id,
					})),
				);
			}

			const validSessionIds = new Set(
				sessions.map((session) => session.session_id),
			);

			exercises = exercises.map((exercise) => ({
				...exercise,
				session_id: validSessionIds.has(exercise.session_id)
					? exercise.session_id
					: null,
			}));

			return {
				...day,
				sessions,
				exercises,
			};
		}),
	};
};

/**
 * Build the `weeks` part of a program save.
 *
 * Weeks the editor never loaded are sent WITHOUT a `days` key, and
 * `weeks_partial` tells the backend to put their stored days back before it does
 * anything else. Sending `days: []` for them instead would wipe them, so this is
 * the one place allowed to assemble a week payload.
 *
 * `options.savedWeeks` are the weeks as they were at the last load/save (the
 * editor's saved snapshot). A loaded week that is identical to its saved copy
 * travels as a skeleton too: the backend restores its stored days, which are
 * exactly what it holds already. On a 23-week program this turns a 160 KB PUT
 * into ~30 KB when one week changed (docs/mongo-scale/PLAN.md, Fase 6-light).
 * A week with no saved copy (new, or renamed id) always travels in full.
 */
export const buildWeeksSavePayload = (weeks, normalizeDays, options = {}) => {
	const weekList = Array.isArray(weeks) ? weeks : [];
	const savedWeeks = Array.isArray(options.savedWeeks) ? options.savedWeeks : [];
	const savedById = new Map(
		savedWeeks
			.filter((week) => week && week.week_id)
			.map((week) => [week.week_id, JSON.stringify(week)]),
	);
	let partial = false;

	const payloadWeeks = weekList.map((week) => {
		if (isWeekContentLoaded(week)) {
			const unchanged =
				week.week_id &&
				savedById.has(week.week_id) &&
				savedById.get(week.week_id) === JSON.stringify(week);
			if (!unchanged) {
				return {
					...week,
					days: normalizeDays ? normalizeDays(week.days) : week.days,
				};
			}
		}
		partial = true;
		const { days, days_omitted, day_dates, ...metadata } = week;
		return metadata;
	});

	return { weeks: payloadWeeks, weeks_partial: partial };
};

export const findCurrentDatedWeekIndex = (weeks) => {
	const { start, end } = getCurrentCalendarWeekRange();
	return (weeks || []).findIndex((week) =>
		getWeekDayDates(week).some((date) => date >= start && date <= end),
	);
};

export const normalizeLoadExpression = (value) => {
	if (value === "" || value === null || value === undefined) return null;
	const raw = String(value).trim();
	if (!raw) return null;
	const compact = raw.replace(/\s+/g, "");
	if (/[a-zA-Z]/.test(compact)) return raw;
	if (/^[\d.,\-\/+%]+$/.test(compact)) {
		const hadPercent = compact.includes("%");
		const normalizedBase = compact
			.replace(/,/g, ".")
			.replace(/\//g, "-")
			.replace(/%+/g, "");
		const normalized = hadPercent ? `${normalizedBase}%` : normalizedBase;
		return normalized || null;
	}
	return raw;
};

export const normalizeSetExpression = (value) => {
	if (value === "" || value === null || value === undefined) {
		return { sets: 1, sets_label: null };
	}
	const raw = String(value).trim();
	if (!raw) return { sets: 1, sets_label: null };
	const compact = raw.replace(/\s+/g, "");

	if (/^\d+$/.test(compact)) {
		return { sets: Number(compact), sets_label: null };
	}

	if (/^\d+[\/-]\d+$/.test(compact)) {
		const values = compact.split(/[\/-]/).map((part) => Number(part));
		if (values.some((n) => Number.isNaN(n) || n <= 0)) {
			return { sets: 1, sets_label: null };
		}
		return {
			sets: Math.max(...values),
			sets_label: raw,
		};
	}

	const numeric = Number(compact);
	if (Number.isFinite(numeric) && numeric > 0) {
		return { sets: numeric, sets_label: null };
	}

	return { sets: 1, sets_label: null };
};

export const createStrengthSetRow = (overrides = {}) => ({
	sets: "",
	sets_label: null,
	reps: "",
	load_percentage: null,
	load_reference_exercise_id: null,
	load_reference_exercise_name: null,
	rest_seconds: null,
	rpe: null,
	rir: null,
	notes: "",
	...overrides,
});

export const createEmomRoundTemplate = (overrides = {}) => ({
	round_id: `emom_round_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`,
	assignment_type: "cycle",
	label: "",
	content: "",
	input_mode: "custom", // 'custom' (free text) or 'library' (linked items)
	items: [], // [{ exercise_id, exercise_name, reps, load, load_male, load_female, notes, load_reference_exercise_id, load_reference_exercise_name }]
	...overrides,
});

export const normalizeEmomRoundTemplates = (exercise) => {
	const rounds = Array.isArray(exercise?.emom_rounds)
		? exercise.emom_rounds
		: Array.isArray(exercise?.emom_round_templates)
			? exercise.emom_round_templates
			: [];
	if (rounds.length > 0) {
		return rounds.map((r) => {
			// Determine input mode: if it has items with exercise_id and NOT custom, it's library mode
			const hasLibraryItems =
				Array.isArray(r?.items) &&
				r.items.some(
					(it) =>
						it &&
						!it.is_custom &&
						(it.exercise_id || it.exercise_name || it.name || it.movement),
				);
			const inputMode = hasLibraryItems ? "library" : "custom";

			return createEmomRoundTemplate({
				round_id: r?.round_id,
				assignment_type: r?.assignment_type || "cycle",
				label: r?.custom_label || r?.label || "",
				input_mode: inputMode,
				items:
					inputMode === "library"
						? r.items.map((it) => ({
								exercise_id: it.exercise_id,
								exercise_name: it.exercise_name || it.name || it.movement || "",
								reps: it.reps || "",
								load: it.load || "",
								...splitEmomItemLoad(it.load),
								// La nota del movimento la legge l'atleta: se la
								// normalizzazione la lascia fuori, riaprire l'esercizio
								// nel builder la cancella senza che nessuno la veda.
								notes: it.notes || "",
								load_reference_exercise_id:
									it.load_reference_exercise_id || null,
								load_reference_exercise_name:
									it.load_reference_exercise_name || null,
							}))
						: [],
				content:
					r?.content ||
					r?.custom_text ||
					(inputMode === "custom" && Array.isArray(r?.items)
						? r.items
								.map((it) => {
									if (it?.is_custom) return it?.custom_text || "";
									const parts = [];
									if (it?.reps) parts.push(it.reps);
									const itemName =
										it?.exercise_name || it?.name || it?.movement || "";
									if (itemName) parts.push(itemName);
									if (it?.load) parts.push(`@${it.load}`);
									return parts.join(" ").trim();
								})
								.filter(Boolean)
								.join(" + ")
						: ""),
			});
		});
	}

	return [];
};

export const hasConfiguredEmomRoundTemplates = (rounds) =>
	(rounds || []).some(
		(r) =>
			(r?.input_mode === "custom" && Boolean(r?.content?.trim())) ||
			(r?.input_mode === "library" && (r?.items || []).length > 0),
	);

export const serializeEmomRoundTemplates = (rounds) =>
	(rounds || [])
		.filter((round) => round && typeof round === "object")
		.map((round) => {
			const isLibrary = round?.input_mode === "library";
			const content = (round?.content || "").trim();

			let items = [];
			if (isLibrary) {
				items = (round.items || []).map((it) => ({
					item_id: null,
					exercise_id: it.exercise_id,
					exercise_name: it.exercise_name,
					reps: it.reps || null,
					load:
						joinEmomItemLoads(it.load_male, it.load_female) || it.load || null,
					load_reference_exercise_id: it.load_reference_exercise_id || null,
					load_reference_exercise_name: it.load_reference_exercise_name || null,
					// Era `null` fisso: salvare un EMOM cancellava la nota del
					// movimento, che l'atleta pero' vede. Ora il builder la mostra,
					// quindi la si salva com'e'.
					notes: it.notes || null,
					is_custom: false,
					custom_text: null,
				}));
			} else if (content) {
				items = [
					{
						item_id: null,
						exercise_id: null,
						exercise_name: null,
						reps: null,
						load: null,
						notes: null,
						is_custom: true,
						custom_text: content,
					},
				];
			}

			return {
				round_id: round?.round_id || null,
				assignment_type: round?.assignment_type || "cycle",
				specific_minutes: round?.specific_minutes || null,
				custom_label: (round?.label || "").trim() || null,
				items,
			};
		});

export const extractPercentageNumbers = (loadStr) => {
	if (!loadStr || typeof loadStr !== "string") return [];
	const matches = loadStr.match(/(\d+(?:\.\d+)?)\s*%/g);
	return matches || [];
};

// --- Endurance pace helpers (running / fartlek). All pure. ---------------

export const normalizeFartlekSegmentType = (segmentType) => {
	if (segmentType === "work") return "fast";
	if (segmentType === "recovery") return "slow";
	if (
		segmentType === "fast" ||
		segmentType === "medium" ||
		segmentType === "slow"
	) {
		return segmentType;
	}
	return "medium";
};

export const parsePaceToSeconds = (paceValue) => {
	const normalized = String(paceValue || "")
		.trim()
		.replace(",", ".")
		.toLowerCase()
		.replace("/km", "")
		.trim();
	if (!normalized) return null;

	const [minutesRaw, secondsRaw = "0"] = normalized.split(":");
	const minutes = Number(minutesRaw);
	const seconds = Number(secondsRaw);
	if (!Number.isFinite(minutes) || !Number.isFinite(seconds)) return null;
	if (minutes < 0 || seconds < 0 || seconds >= 60) return null;
	return Math.round(minutes * 60 + seconds);
};

export const formatPaceFromSeconds = (totalSeconds) => {
	const safeSeconds = Number(totalSeconds);
	if (!Number.isFinite(safeSeconds) || safeSeconds <= 0) return "";
	const rounded = Math.round(safeSeconds);
	const minutes = Math.floor(rounded / 60);
	const seconds = rounded % 60;
	return `${minutes}:${String(seconds).padStart(2, "0")}/km`;
};

export const parseRunningPaceModifier = (modifierValue) => {
	const raw = String(modifierValue ?? "").trim();
	if (!raw) return null;

	if (/^[+-]\s*\d+(?:[\.,]\d+)?\s*(?:\"|''|s|sec|secs)?$/i.test(raw)) {
		const delta = Number(
			raw.replace(/\s*(?:\"|''|s|sec|secs)\s*$/i, "").replace(",", "."),
		);
		if (Number.isFinite(delta)) {
			return { type: "delta", value: delta };
		}
		return null;
	}

	const normalizedPercent = raw.endsWith("%") ? raw.slice(0, -1).trim() : raw;
	if (/^\d+(?:[\.,]\d+)?$/.test(normalizedPercent)) {
		const pct = Number(normalizedPercent.replace(",", "."));
		if (Number.isFinite(pct) && pct > 0) {
			return { type: "percentage", value: pct };
		}
	}

	return null;
};

export const getRunningBenchmarkPaceValue = (
	benchmarkOption,
	fallbackPace = "",
) => {
	const candidates = [
		benchmarkOption?.reference_pace,
		benchmarkOption?.pace_reference_pace,
		benchmarkOption?.target_pace,
		benchmarkOption?.pace,
		benchmarkOption?.benchmark_pace,
		benchmarkOption?.value,
	];

	for (const candidate of candidates) {
		const parsed = parsePaceToSeconds(candidate);
		if (Number.isFinite(parsed) && parsed > 0) {
			return formatPaceFromSeconds(parsed);
		}
	}

	return String(fallbackPace || "").trim();
};

export const resolveRunningReferencedPace = (benchmarkPace, modifierValue) => {
	const benchmarkSeconds = parsePaceToSeconds(benchmarkPace);
	if (!Number.isFinite(benchmarkSeconds) || benchmarkSeconds <= 0) return "";

	const modifier = parseRunningPaceModifier(modifierValue);
	if (!modifier) return "";

	if (modifier.type === "delta") {
		const resolved = benchmarkSeconds + modifier.value;
		return resolved > 0 ? formatPaceFromSeconds(resolved) : "";
	}

	const factor = modifier.value / 100;
	if (!Number.isFinite(factor) || factor <= 0) return "";
	return formatPaceFromSeconds(benchmarkSeconds / factor);
};

export const getRunningPaceModifierHint = (modifierValue, t) => {
	const tr = (key, fallback) =>
		typeof t === "function" ? t(key) : fallback;
	const raw = String(modifierValue ?? "").trim();
	if (!raw)
		return tr("pbPaceModifierSyntax", "Sintassi: 85% oppure +10s / -15s");

	const parsed = parseRunningPaceModifier(raw);
	if (!parsed)
		return tr(
			"pbPaceModifierInvalid",
			"Formato non valido. Esempi: 85% | +10s | -15s",
		);

	if (parsed.type === "percentage") {
		return `${tr("pbBenchmarkPercentage", "Percentuale benchmark")}: ${parsed.value}%`;
	}

	const shown = Number.isInteger(parsed.value)
		? parsed.value
		: Number(parsed.value.toFixed(1));
	const sign = shown > 0 ? "+" : "";
	return `Scarto dal benchmark: ${sign}${shown}s`;
};

export const getRunningTargetPaceMode = (target) => {
	if (target?.target_pace_mode === "reference") return "reference";
	if (target?.target_pace_mode === "manual") return "manual";
	if (String(target?.target_pace || "").trim()) return "manual";
	if (target?.target_pace_reference_benchmark_id) return "reference";
	return "manual";
};

export const getFartlekSegmentPaceMode = (segment) => {
	if (segment?.pace_mode === "reference") return "reference";
	if (segment?.pace_mode === "manual") return "manual";
	if ((segment?.pace || "").trim()) return "manual";
	if (segment?.pace_reference_benchmark_id) return "reference";
	return "manual";
};

// --- Erg/bike type taxonomy + conditioning display labels. All pure. -----

export const ERG_TYPES = [
	{ value: "rowerg", label: "RowErg", labelKey: "ergTypeRowerg", icon: "Waves" },
	{ value: "bikeerg", label: "BikeErg", labelKey: "ergTypeBikeerg", icon: "Bike" },
	{ value: "skierg", label: "SkiErg", labelKey: "ergTypeSkierg", icon: "Timer" },
	{ value: "assault_bike", label: "AssaultBike", labelKey: "ergTypeAssaultBike", icon: "Zap" },
	{ value: "mixed_erg", label: "Misto", labelKey: "ergTypeMixedErg", icon: "Layers" },
	{ value: "custom", label: "Personalizzato", labelKey: "ergTypeCustom", icon: "Pencil" },
	{ value: "benchmark", label: "Benchmark", labelKey: "ergTypeBenchmark", icon: "Target" },
];

export const ERG_CONDITIONING_TYPES = [
	"rowerg_conditioning",
	"bikeerg_conditioning",
	"skierg_conditioning",
	"assault_bike_conditioning",
];

export const ERG_ENDURANCE_TYPES = [
	"rowerg_endurance",
	"bikeerg_endurance",
	"skierg_endurance",
	"assault_bike_endurance",
];

export const ERG_BENCHMARK_TYPES = [
	"rowerg_benchmark",
	"bikeerg_benchmark",
	"skierg_benchmark",
	"assault_bike_benchmark",
];

export const ERG_MACHINE_TYPES = [
	"rowerg",
	"bikeerg",
	"skierg",
	"assault_bike",
];

export const isErgConditioningType = (ergType) =>
	ERG_CONDITIONING_TYPES.includes(ergType);
export const isErgEnduranceType = (ergType) =>
	ERG_ENDURANCE_TYPES.includes(ergType);
export const isErgBenchmarkType = (ergType) =>
	ERG_BENCHMARK_TYPES.includes(ergType);
export const isErgBenchmarkSelection = (ergType) =>
	ergType === "benchmark" || isErgBenchmarkType(ergType);

export const getErgMachineType = (ergType) => {
	const value = String(ergType || "");
	if (value === "custom") return "custom";
	if (value === "benchmark") return "benchmark";
	if (value === "mixed_erg" || value.startsWith("mixed_erg"))
		return "mixed_erg";
	if (value.startsWith("rowerg")) return "rowerg";
	if (value.startsWith("bikeerg")) return "bikeerg";
	if (value.startsWith("skierg")) return "skierg";
	if (value.startsWith("assault_bike")) return "assault_bike";
	return value;
};

export const isErgMixedType = (ergType) =>
	getErgMachineType(ergType) === "mixed_erg";
export const isErgMachineSubtypeType = (ergType) =>
	ERG_MACHINE_TYPES.includes(getErgMachineType(ergType));

export const getErgSubtype = (ergType) => {
	if (isErgBenchmarkSelection(ergType)) return "benchmark";
	if (isErgConditioningType(ergType)) return "conditioning";
	return "endurance";
};

export const getErgTypeLabel = (ergType) => {
	const machine = getErgMachineType(ergType);
	if (machine === "custom") return "Personalizzato";
	if (machine === "mixed_erg") return "Misto";
	if (ERG_MACHINE_TYPES.includes(machine)) {
		const machineLabel =
			ERG_TYPES.find((x) => x.value === machine)?.label || machine;
		const subtype = getErgSubtype(ergType);
		const subtypeLabel =
			subtype === "conditioning"
				? "Ripetute"
				: subtype === "benchmark"
					? "Benchmark"
					: "Fondo";
		return `${machineLabel} - ${subtypeLabel}`;
	}
	return ERG_TYPES.find((x) => x.value === ergType)?.label || ergType;
};

/**
 * Localized twin of `getErgTypeLabel`, for display only.
 *
 * `getErgTypeLabel` stays as it is: its output also feeds generated
 * `exercise_name` values, which are persisted and must not move with the UI
 * language. Pass `t` here when you are only rendering.
 *
 * @param {string} ergType erg type (optionally machine_subtype)
 * @param {Function} t translator from `useLanguage()`
 * @returns {string} localized label
 */
export const getErgTypeLabelLocalized = (ergType, t) => {
	const machine = getErgMachineType(ergType);
	if (machine === "custom") return t("ergTypeCustom");
	if (machine === "mixed_erg") return t("ergTypeMixedErg");
	if (ERG_MACHINE_TYPES.includes(machine)) {
		const entry = ERG_TYPES.find((x) => x.value === machine);
		const machineLabel = entry?.labelKey ? t(entry.labelKey) : machine;
		const subtype = getErgSubtype(ergType);
		const subtypeLabel =
			subtype === "conditioning"
				? t("pbErgModeConditioning")
				: subtype === "benchmark"
					? t("ergTypeBenchmark")
					: t("pbErgModeEndurance");
		return `${machineLabel} - ${subtypeLabel}`;
	}
	const fallback = ERG_TYPES.find((x) => x.value === ergType);
	return fallback?.labelKey ? t(fallback.labelKey) : ergType;
};

// Names the running/swim/bike/erg forms generate from the type ("Erg - RowErg -
// Ripetute", "Running Benchmark - 5K", or the bare "Erg" of the save fallback).
const AUTO_ENDURANCE_NAME = /^(running|swim|bike|erg)( benchmark)?( - |$)/i;

/**
 * True when an endurance exercise name is empty or one the forms generated,
 * i.e. safe to replace when the coach picks another type. A name typed by the
 * coach or written by the AI ("Row Erg - Cruise intervals") is not.
 */
export const isAutoEnduranceName = (name) => {
	const value = String(name || "").trim();
	return !value || AUTO_ENDURANCE_NAME.test(value);
};

/** The generated name for the new type, unless the current one is custom. */
export const nextEnduranceName = (currentName, generatedName) =>
	isAutoEnduranceName(currentName) ? generatedName : currentName;

export const getConditioningTypeLabel = (conditioningType) =>
	CONDITIONING_TYPES.find((x) => x.value === conditioningType)?.label ||
	conditioningType ||
	"Custom";

export const formatConditioningDisplayName = (
	exerciseName,
	conditioningType,
) => {
	const normalizedConditioningType = String(conditioningType || "")
		.trim()
		.toLowerCase();
	const isCustomConditioning =
		normalizedConditioningType === "free" ||
		normalizedConditioningType === "custom";
	const typeLabel = getConditioningTypeLabel(conditioningType);
	const typeLower = typeLabel.toLowerCase();
	const rawName = (exerciseName || "").trim();

	// Custom conditioning has its own simpler format: "Conditioning" if no
	// name, "Conditioning - <name>" otherwise. We also strip any legacy
	// "Custom" pollution from previously-saved names so the coach never
	// sees the word "Custom" again either.
	if (isCustomConditioning) {
		const cleaned = rawName
			.replace(/^Conditioning\s*-\s*/i, "")
			.replace(/\s*-\s*Custom\s*$/i, "")
			.replace(/^(Custom|Conditioning)$/i, "")
			.trim();
		return cleaned ? `Conditioning - ${cleaned}` : "Conditioning";
	}

	if (!rawName) return `Conditioning - ${typeLabel}`;

	const parts = rawName
		.split("-")
		.map((p) => p.trim())
		.filter(Boolean);

	// If name is exactly the type label
	if (rawName.toLowerCase() === typeLower) {
		return `Conditioning - ${typeLabel}`;
	}

	// If name is already "type - type"
	if (
		parts.length === 2 &&
		parts[0].toLowerCase() === typeLower &&
		parts[1].toLowerCase() === typeLower
	) {
		return `Conditioning - ${typeLabel}`;
	}

	// If name ends with the type and has other parts
	if (
		parts.length >= 2 &&
		parts[parts.length - 1].toLowerCase() === typeLower
	) {
		const basePart = parts.slice(0, -1).join(" - ").trim();
		if (!basePart || basePart.toLowerCase() === typeLower) {
			return `Conditioning - ${typeLabel}`;
		}
		return `${basePart} - ${typeLabel}`;
	}

	// If no conditioning_type provided, try to normalize from habituated name
	if (!conditioningType && parts.length >= 2) {
		const potentialType = parts[parts.length - 1].toLowerCase();
		const knownTypes = CONDITIONING_TYPES.map((ct) => ct.label.toLowerCase());
		if (knownTypes.includes(potentialType)) {
			const basePart = parts.slice(0, -1).join(" - ").trim();
			if (basePart && basePart.toLowerCase() !== potentialType) {
				return `${basePart} - ${parts[parts.length - 1]}`;
			}
		}
	}

	return `${rawName} - ${typeLabel}`;
};

// --- Exercise type / discipline taxonomy (shared data) + rest helper. Pure. --

export const normalizeRestFieldValue = (value) =>
	normalizeSmartRestInput(value) || "";

/**
 * Tassonomia dei tipi di esercizio.
 *
 * Il campo `domain` decide chi li vede: un coach di solo tiro non deve trovarsi
 * davanti Forza e Conditioning, e uno di sola preparazione fisica non deve
 * vedere Dry Fire e Live Fire.
 *
 * **Questa costante resta sempre completa.** Il filtro va applicato al punto
 * di rendering del selettore, con `filterByDomain(EXERCISE_TYPES, user)`
 * (`lib/domainAccess.js`), mai amputando la lista alla fonte: è usata anche in
 * percorsi di sola lettura, e un coach senza l'ambito che apre un programma
 * condiviso deve comunque poter risolvere label e icona degli esercizi che non
 * potrebbe creare. Spec: docs/shooting/07-domini-e-abilitazioni.md §5.
 */
export const EXERCISE_TYPES = [
	{
		value: "strength",
		label: "Forza", labelKey: "exTypeStrength",
		icon: "Dumbbell",
		color: "text-volt",
		domain: "physical",
	},
	{
		value: "running",
		label: "Running", labelKey: "exTypeRunning",
		icon: "Footprints",
		color: "text-green-400",
		domain: "physical",
	},
	{
		value: "swim",
		label: "Swim", labelKey: "exTypeSwim",
		icon: "Waves",
		color: "text-blue-400",
		domain: "physical",
	},
	{
		value: "bike",
		label: "Bike", labelKey: "exTypeBike",
		icon: "Bike",
		color: "text-orange-400",
		domain: "physical",
	},
	{
		value: "erg",
		label: "Erg", labelKey: "exTypeErg",
		icon: "Timer",
		color: "text-cyan-400",
		domain: "physical",
	},
	{
		value: "conditioning",
		label: "Conditioning", labelKey: "exTypeConditioning",
		icon: "Zap",
		color: "text-yellow-400",
		domain: "physical",
	},
	{
		value: "dry_fire",
		label: "Dry Fire", labelKey: "exTypeDryFire",
		icon: "Crosshair",
		color: "text-sky-400",
		domain: "shooting",
	},
	{
		value: "live_fire",
		label: "Live Fire", labelKey: "exTypeLiveFire",
		icon: "Target",
		color: "text-red-400",
		domain: "shooting",
	},
];

/**
 * Gli ambiti di un programma, dedotti dai suoi esercizi.
 *
 * Si calcola **al salvataggio, lato client**, dove il programma intero è già in
 * memoria: ricavarlo dal database costerebbe leggere tutte le settimane nella
 * lista programmi — la pagina più consultata dal coach — e mantenerlo
 * incrementalmente sui 13 punti di scrittura del backend lo renderebbe stantio
 * in silenzio al primo punto dimenticato.
 *
 * Così il campo esiste ma il coach non lo tocca: inserisce un esercizio di tiro
 * e il programma diventa di tiro, ne aggiunge uno fisico e diventa ibrido,
 * toglie l'ultimo drill e torna fisico. Resta la possibilità di forzarlo a mano
 * (`domains_auto: false`).
 */
export const computeProgramDomains = (program, { partial = false } = {}) => {
	const domainByType = new Map(
		EXERCISE_TYPES.map((entry) => [entry.value, entry.domain]),
	);
	const found = new Set();

	const visitExercises = (exercises) => {
		if (!Array.isArray(exercises)) return;
		for (const exercise of exercises) {
			const domain = domainByType.get(exercise?.exercise_type);
			// Un tipo sconosciuto (dato vecchio, o un tipo aggiunto dopo) non
			// deve far sparire il programma da nessun filtro: vale physical,
			// che è il default storico.
			found.add(domain || "physical");
		}
	};

	for (const week of program?.weeks || []) {
		for (const day of week?.days || []) {
			visitExercises(day?.exercises);
			for (const session of day?.sessions || []) {
				visitExercises(session?.exercises);
			}
		}
	}

	// Su un salvataggio **parziale** le settimane non caricate viaggiano senza
	// `days`: i loro esercizi non si vedono. Allora si può solo AGGIUNGERE a
	// quello che si sapeva già, mai togliere — se no aprire la settimana 1 di un
	// programma di 12 cancellerebbe l'ambito delle altre undici.
	if (partial) {
		for (const domain of program?.domains || []) found.add(domain);
	}

	// Ordine stabile: il badge non deve cambiare disposizione fra due salvataggi.
	return ["physical", "shooting"].filter((domain) => found.has(domain));
};

/** Etichetta dell'ambito di un programma, per il badge nella libreria. */
export const programDomainLabel = (domains) => {
	const list = Array.isArray(domains) ? domains : [];
	const hasPhysical = list.includes("physical");
	const hasShooting = list.includes("shooting");
	if (hasPhysical && hasShooting) return "Ibrido";
	if (hasShooting) return "Tiro";
	if (hasPhysical) return "Fisico";
	return null;
};

/** I due tipi di tiro, per i branch che devono trattarli insieme. */
export const SHOOTING_EXERCISE_TYPES = ["dry_fire", "live_fire"];

export const isShootingExerciseType = (type) =>
	SHOOTING_EXERCISE_TYPES.includes(type);

/**
 * Tipi di bersaglio. Sono gli elementi standard IPSC, e sono anche quelli che
 * un eventuale stage designer disporrebbe sulla piantina: tenerli qui come
 * vocabolario unico evita che l'editor ne introduca un secondo.
 */
export const SHOOTING_TARGET_TYPES = [
	{ value: "ipsc", label: "Sagoma IPSC", labelKey: "shootTargetIpsc" },
	{ value: "mini_ipsc", label: "Mini IPSC", labelKey: "shootTargetMiniIpsc" },
	{ value: "idpa", label: "Sagoma IDPA", labelKey: "shootTargetIdpa" },
	{ value: "no_shoot", label: "No-shoot IPSC", labelKey: "shootTargetNoShoot" },
	{ value: "no_shoot_idpa", label: "No-shoot IDPA", labelKey: "shootTargetNoShootIdpa" },
	{ value: "bobber", label: "Bobber", labelKey: "shootTargetBobber" },
	{ value: "popper", label: "Popper", labelKey: "shootTargetPopper" },
	{ value: "mini_popper", label: "Mini popper", labelKey: "shootTargetMiniPopper" },
	{ value: "popper_large", label: "Popper grande", labelKey: "shootTargetPopperLarge" },
	{ value: "plate", label: "Piatto tondo", labelKey: "shootTargetPlate" },
	{ value: "plate_large", label: "Piatto grande", labelKey: "shootTargetPlateLarge" },
	// Gli elementi che lo stage designer sa posare e che la tabella non
	// sapeva nominare: un drill disegnato con un piatto rettangolare veniva
	// riletto come «plate_rect» nudo.
	{ value: "micro_ipsc", label: "Micro IPSC", labelKey: "shootTargetMicroIpsc" },
	{ value: "plate_rect", label: "Piatto 15×15", labelKey: "shootTargetPlateRect" },
	{ value: "plate_rect_large", label: "Piatto 30×30", labelKey: "shootTargetPlateRectLarge" },
	{ value: "plate_rack", label: "Plate rack", labelKey: "shootTargetPlateRack" },
	{ value: "no_shoot_metal", label: "No-shoot metallo", labelKey: "shootTargetNoShootMetal" },
	{ value: "custom", label: "Altro", labelKey: "shootTargetCustom" },
];

/**
 * Riepilogo leggibile dei bersagli: "3× IPSC @10m · 1× no-shoot @7m".
 * Con distanze diverse è l'unico modo onesto di descrivere il drill.
 */
/**
 * I bersagli come li legge chi deve allestire: `3× Sagoma IPSC @10m`.
 *
 * Il raggruppamento è il punto. Lo stage planner produce **una riga per sagoma
 * trascinata**, quindi tre sagome uguali erano tre righe identiche in fila —
 * illeggibili per l'atleta. `groupTargets` le collassa; questa funzione sapeva
 * già scrivere `3×`, non aveva niente da raggruppare.
 */
export const formatShootingTargets = (targets, t) => {
	if (!Array.isArray(targets) || targets.length === 0) return "";
	return groupTargets(targets)
		.map((target) => {
			const entry = SHOOTING_TARGET_TYPES.find(
				(item) => item.value === target.target_type,
			);
			const label =
				(typeof t === "function" && entry?.labelKey
					? t(entry.labelKey)
					: entry?.label) ||
				findElement(target.target_type)?.label ||
				target.target_type;
			const count = target.count > 1 ? `${target.count}× ` : "";
			const distance = target.distance_m ? ` @${target.distance_m}m` : "";
			return `${count}${label}${distance}`;
		})
		.join(" · ");
};

/**
 * La piantina da mostrare per un drill, con la sua provenienza.
 *
 * Due livelli convivono di proposito:
 *  - la **sessione** porta l'allestimento della giornata — lo stage si monta
 *    una volta la mattina e i drill ne usano dei settori;
 *  - il **drill** può averne una propria, ed è il caso dei drill di libreria,
 *    che si portano dietro il loro stage design.
 *
 * Il drill vince sempre sulla sessione: se qualcuno si è preso la briga di
 * disegnare lo stage di quel drill, è perché è diverso da quello generale.
 */
export const resolveStageImage = (exercise, session) => {
	const own = exercise?.shooting?.stage_image_url;
	if (own) {
		return {
			url: own,
			source: "exercise",
			sector: exercise?.shooting?.stage_sector || null,
		};
	}
	const shared = session?.stage_image_url;
	if (shared) {
		return {
			url: shared,
			source: "session",
			sector: exercise?.shooting?.stage_sector || null,
			notes: session?.stage_notes || null,
		};
	}
	return null;
};

/**
 * Un risultato di tiro contiene qualcosa da salvare?
 *
 * La validazione "compila almeno un campo" del diario guarda peso, ripetizioni,
 * tempo e punteggio: nessuno dei quali esiste in un risultato di tiro, che vive
 * dentro `shooting`. Senza questa funzione il salvataggio veniva rifiutato con
 * "compila almeno un campo" anche con quattro stringhe piene.
 *
 * Una stringa conta se ha un tempo **o** dei colpi: al poligono capita di
 * registrare i colpi e recuperare il tempo dopo, e capita di segnare un tempo
 * su un drill che non si scora.
 */
export const hasShootingResultContent = (shooting) => {
	if (!shooting || typeof shooting !== "object") return false;

	// Formato libero: non c'è nulla da misurare, l'autovalutazione è il dato.
	if (shooting.prescription_format === "free") {
		return Number(shooting.self_rating) > 0;
	}

	if (shooting.mode === "dry_fire") {
		return (shooting.times || []).some(
			(time) => String(time ?? "").trim() !== "",
		);
	}

	// `strings` è il nome storico della lista: si legge ancora, perché i
	// documenti salvati prima della rinomina ce l'hanno.
	return (shooting.series || shooting.strings || []).some((series) => {
		if (String(series?.time_seconds ?? "").trim() !== "") return true;
		// Il dettaglio per bersaglio è il posto in cui finiscono gli impatti
		// quando lo stage ha bersagli posizionati: senza guardarlo, una scheda
		// compilata a tocchi risultava vuota e il salvataggio veniva rifiutato.
		const counts = [
			...Object.values(series?.hits || {}),
			...Object.values(series?.penalties || {}),
			...(series?.targets || []).flatMap((target) => [
				...Object.values(target?.hits || {}),
				...Object.values(target?.penalties || {}),
			]),
		];
		return counts.some((count) => Number(count) > 0);
	});
};

/**
 * Tempo di tiro come **secondi + centesimi**, due campi separati.
 *
 * Un cronometro da tiro legge 4.82, non 4.8: i centesimi sono il dato, non un
 * dettaglio — fra 4.82 e 4.89 c'è la differenza fra due piazzamenti. Un campo
 * decimale unico su telefono obbliga a cercare il punto sulla tastiera
 * numerica, e sbagliarlo dà 482 secondi senza che nulla lo segnali.
 *
 * Stesso ragionamento del MM:SS già usato altrove, con l'unità in meno.
 */
export const splitSecondsHundredths = (value) => {
	if (value === null || value === undefined || value === "") {
		return { seconds: "", hundredths: "" };
	}
	const parsed = Number(value);
	if (!Number.isFinite(parsed) || parsed < 0) {
		return { seconds: "", hundredths: "" };
	}
	const whole = Math.floor(parsed);
	// `Math.round` e non `Math.floor`: 4.8199999 dal float deve tornare 82.
	const hundredths = Math.round((parsed - whole) * 100);
	// L'arrotondamento può produrre 100: allora si riporta sul secondo.
	if (hundredths >= 100) {
		return { seconds: String(whole + 1), hundredths: "00" };
	}
	return {
		seconds: String(whole),
		hundredths: String(hundredths).padStart(2, "0"),
	};
};

export const joinSecondsHundredths = (seconds, hundredths) => {
	const wholeText = String(seconds ?? "").trim();
	const fractionText = String(hundredths ?? "").trim();
	if (!wholeText && !fractionText) return "";
	const whole = Number(wholeText || 0);
	// "5" nei centesimi vale 50, non 5: è il comportamento di un decimale, e
	// chi digita in fretta si ferma alla prima cifra.
	const fraction = Number((fractionText || "0").padEnd(2, "0").slice(0, 2));
	if (!Number.isFinite(whole) || !Number.isFinite(fraction)) return "";
	return String(whole + fraction / 100);
};

/**
 * Un esercizio di tiro non ancora salvabile.
 *
 * **Il sistema di punteggio è obbligatorio sul live fire strutturato.** Senza,
 * il risultato dell'atleta non è calcolabile: il backend lo rifiuta al
 * salvataggio, quindi lasciar creare l'esercizio significa solo spostare
 * l'errore più in là, quando il tiratore è già al poligono. Prima c'era un
 * avviso ambra, che si ignora.
 *
 * Non vale per il dry fire (non ha punteggio) né per il formato libero (non
 * si scora).
 */
export const isShootingExerciseIncomplete = (exercise) => {
	if (exercise?.exercise_type !== "live_fire") return false;
	const shooting = exercise.shooting || {};
	if (shooting.prescription_format === "free") return false;
	return !shooting.scoring_system_id;
};

/**
 * L'arma con cui si sta programmando questa giornata.
 *
 * Se gli esercizi di tiro del giorno sono di pistola, il prossimo nasce già
 * con la pistola. Si guarda l'**ultimo inserito**, non il primo: è quello che
 * dice cosa si sta facendo adesso.
 */
export const suggestedFirearmType = (exercises) => {
	const list = Array.isArray(exercises) ? exercises : [];
	for (let i = list.length - 1; i >= 0; i -= 1) {
		const type = list[i]?.shooting?.firearm_type;
		if (type) return type;
	}
	return null;
};

/**
 * Condizione di pronto dell'**arma** (FITDS 8.1).
 *
 * Non è un elenco inventato: sono le condizioni che il regolamento descrive,
 * una per tipo di azione, più i due casi fuori dall'ordinario (camera vuota,
 * arma scarica) e quello da tavolo. Averle in elenco invece che a testo
 * libero è ciò che le rende uguali su due drill diversi — e il testo libero
 * resta disponibile in coda per quello che l'elenco non copre.
 */
export const START_CONDITION_FIREARM = [
	// Le tre «condizioni» con cui i tiratori chiamano da sempre la partenza
	// in fondina — Cond. 1, 2, 3 — vengono prima, perché sono quelle che si
	// usano. «Secondo la division» perché cane e sicura li decide quella.
	{
		value: "holster_loaded",
		label: "Cond. 1 — carica in fondina, secondo la division",
	},
	{
		value: "empty_chamber",
		label: "Cond. 2 — caricatore inserito, colpo non camerato, in fondina",
	},
	{
		value: "unloaded",
		label: "Cond. 3 — scarica in fondina, senza caricatore",
	},
	// Il lavoro di precisione non parte dalla fondina: l'arma è già in mano,
	// carica, colpo in canna e cane armato. Mancava, e si scriveva a mano.
	{
		value: "ready_cocked",
		label: "Arma armata — in mano, carica, colpo in canna, cane armato",
	},
	{ value: "sa", label: "SA: camera carica, cane armato, sicura inserita" },
	{ value: "da", label: "DA: camera carica, cane abbattuto" },
	{
		value: "mixed",
		label: "Azione mista: cane abbattuto, o armato con sicura",
	},
	{ value: "on_table", label: "Sul tavolo, come da briefing" },
];

/**
 * Condizione di pronto del **tiratore** (FITDS 8.2).
 *
 * La prima voce è il **default normativo** della 8.2.2: è ciò che vale quando
 * il briefing non dice altro, quindi è anche il valore giusto da proporre.
 */
export const START_CONDITION_SHOOTER = [
	{
		value: "default",
		label: "Eretto, rivolto ai parapalle, braccia lungo i fianchi",
	},
	{ value: "hands_shoulders", label: "Mani sopra le spalle" },
	{ value: "hands_head", label: "Mani sulla testa" },
	{ value: "hands_table", label: "Mani sul tavolo" },
	{ value: "wrists_marked", label: "Polsi sui segni" },
	{ value: "back_to_targets", label: "Di spalle ai bersagli" },
	{ value: "seated", label: "Seduto" },
	{ value: "facing_downrange_hands_relaxed", label: "Mani rilassate ai lati" },
	// Allenamento con l'arma già in mano: non è una partenza di gara ma è
	// quella del lavoro tecnico, e mancava del tutto.
	{ value: "gun_low_ready", label: "Arma in mano — low ready" },
	{ value: "gun_high_ready", label: "Arma in mano — high ready" },
	{ value: "gun_compressed_ready", label: "Arma in mano — compressed ready" },
	{ value: "gun_45", label: "Arma in mano — a 45°" },
	{ value: "gun_aimed", label: "Arma in mano — in puntamento sul bersaglio" },
];

/** L'etichetta di una condizione: se non è in elenco, è testo libero. */
export const startConditionLabel = (options, value) =>
	options.find((option) => option.value === value)?.label || value || "";

/** Focus di un drill: è il taglio con cui il coach ragiona sul metodo. */
export const SHOOTING_FOCUS_OPTIONS = [
	{ value: "draw", label: "Estrazione", labelKey: "shootFocusDraw" },
	{ value: "transitions", label: "Transizioni", labelKey: "shootFocusTransitions" },
	{ value: "reload", label: "Ricarica", labelKey: "shootFocusReload" },
	{ value: "movement", label: "Movimento", labelKey: "shootFocusMovement" },
	{ value: "accuracy", label: "Precisione", labelKey: "shootFocusAccuracy" },
	{ value: "stage", label: "Sequenza / stage", labelKey: "shootFocusStage" },
	{ value: "manipulation", label: "Maneggio", labelKey: "shootFocusManipulation" },
];

export const RUNNING_TYPES = [
	{ value: "lungo", label: "Lungo", labelKey: "runTypeLungo" },
	{ value: "progressivo", label: "Progressivo", labelKey: "runTypeProgressivo" },
	{ value: "ripetute", label: "Ripetute", labelKey: "runTypeRipetute" },
	{ value: "fartlek", label: "Fartlek", labelKey: "runTypeFartlek" },
	{ value: "salite", label: "Salite", labelKey: "runTypeSalite" },
	{ value: "benchmark", label: "Benchmark", labelKey: "runTypeBenchmark" },
	{ value: "custom", label: "Personalizzato", labelKey: "runTypeCustom" },
];

export const SWIM_TYPES = [
	{ value: "intervals", label: "Serie", labelKey: "swimTypeIntervals" },
	{ value: "endurance", label: "Aerobica", labelKey: "swimTypeEndurance" },
	{ value: "technique", label: "Tecnica", labelKey: "swimTypeTechnique" },
	{ value: "drill", label: "Esercitazioni", labelKey: "swimTypeDrill" },
	{ value: "threshold", label: "Soglia", labelKey: "swimTypeThreshold" },
	{ value: "sprint", label: "Sprint", labelKey: "swimTypeSprint" },
	{ value: "mixed", label: "Completo", labelKey: "swimTypeMixed" },
	{ value: "recovery", label: "Recupero", labelKey: "swimTypeRecovery" },
	{ value: "benchmark", label: "Benchmark", labelKey: "swimTypeBenchmark" },
	{ value: "custom", label: "Personalizzato", labelKey: "swimTypeCustom" },
];

export const SWIM_STROKES = [
	{ value: "stile_libero", label: "Stile Libero", labelKey: "swimStrokeStileLibero" },
	{ value: "dorso", label: "Dorso", labelKey: "swimStrokeDorso" },
	{ value: "rana", label: "Rana", labelKey: "swimStrokeRana" },
	{ value: "delfino", label: "Delfino", labelKey: "swimStrokeDelfino" },
	{ value: "misto", label: "Misto", labelKey: "swimStrokeMisto" },
];

export const SWIM_EQUIPMENT_OPTIONS = [
	{ value: "pullbuoy", label: "Pullbuoy", labelKey: "swimGearPullbuoy" },
	{ value: "palette", label: "Palette", labelKey: "swimGearPalette" },
	{ value: "pinne", label: "Pinne", labelKey: "swimGearPinne" },
	{ value: "tavoletta", label: "Tavoletta", labelKey: "swimGearTavoletta" },
	{ value: "snorkel", label: "Snorkel", labelKey: "swimGearSnorkel" },
	{ value: "elastico", label: "Elastico", labelKey: "swimGearElastico" },
];

export const BIKE_TYPES = [
	{ value: "endurance", label: "Fondo", labelKey: "bikeTypeEndurance" },
	{ value: "tempo", label: "Tempo", labelKey: "bikeTypeTempo" },
	{ value: "threshold", label: "Soglia (FTP)", labelKey: "bikeTypeThreshold" },
	{ value: "intervals", label: "Intervalli", labelKey: "bikeTypeIntervals" },
	{ value: "vo2max", label: "VO2max", labelKey: "bikeTypeVo2max" },
	{ value: "hill_repeats", label: "Salite", labelKey: "bikeTypeHillRepeats" },
	{ value: "sprint", label: "Sprint", labelKey: "bikeTypeSprint" },
	{ value: "recovery", label: "Recupero", labelKey: "bikeTypeRecovery" },
	{ value: "benchmark", label: "Benchmark", labelKey: "bikeTypeBenchmark" },
	{ value: "custom", label: "Personalizzato", labelKey: "bikeTypeCustom" },
];

export const BIKE_INTERVAL_TYPES = [
	"intervals",
	"hill_repeats",
	"threshold",
	"vo2max",
];

export const BIKE_SIMPLE_TYPES = ["endurance", "tempo", "sprint", "recovery"];

export const BIKE_LIKE_INTERVAL_TYPES = [
	...BIKE_INTERVAL_TYPES,
	...ERG_CONDITIONING_TYPES,
];

export const BIKE_LIKE_SIMPLE_TYPES = [
	...BIKE_SIMPLE_TYPES,
	...ERG_ENDURANCE_TYPES,
];

// --- ERG Metrics Labels: Return correct units + placeholders based on machine type ---
/**
 * Returns measurement unit labels and placeholders for ERG interval fields.
 * Each ERG machine (RowErg, BikeErg, SkiErg, AssaultBike) has different cadence units:
 * - RowErg / SkiErg: SPM (Strokes Per Minute)
 * - BikeErg / AssaultBike: RPM (Revolutions Per Minute)
 * Power is always in Watts (never use FTP label for RowErg/SkiErg since that's cycling-specific).
 */
export const getErgMetricsLabels = (bikeType) => {
	const machine = getErgMachineType(bikeType);

	// RowErg and SkiErg use SPM (Strokes Per Minute)
	if (machine === "rowerg" || machine === "skierg") {
		return {
			cadencePlaceholder: "90-95 spm",
			cadenceHint: "Colpi al minuto",
			powerPlaceholder: "250W",
			powerHint: "Watt",
		};
	}

	// BikeErg and AssaultBike use RPM (Revolutions Per Minute)
	if (machine === "bikeerg" || machine === "assault_bike") {
		return {
			cadencePlaceholder: "90-95 rpm",
			cadenceHint: "Giri al minuto",
			powerPlaceholder: "250W",
			powerHint: "Watt",
		};
	}

	// Fallback for unknown/custom types
	return {
		cadencePlaceholder: "90-95",
		cadenceHint: "Cadenza",
		powerPlaceholder: "250W",
		powerHint: "Potenza",
	};
};

// --- Compact prescription summary -------------------------------------------
//
// Renders any program exercise as a single short line ("5×3 · 85% · R 2'00\"").
// Used by the read-only reference panel, where a whole week has to be scannable
// at a glance; it deliberately summarises rather than reproducing the editor.
// Never throws: unknown/incomplete exercises simply yield a shorter string.

const joinParts = (parts, separator = " · ") =>
	parts
		.map((part) => (part === null || part === undefined ? "" : String(part)))
		.map((part) => part.trim())
		.filter(Boolean)
		.join(separator);

const formatDistanceValue = (meters) => {
	const value = Number(meters);
	if (!Number.isFinite(value) || value <= 0) return "";
	if (value >= 1000) {
		const km = value / 1000;
		return `${Number.isInteger(km) ? km : km.toFixed(1)}km`;
	}
	return `${value}m`;
};

// Loads are free text ("85", "85%", "BW", "60kg"): only bare numbers get a "%".
const formatLoadValue = (load) => {
	const text = String(load ?? "").trim();
	if (!text) return "";
	return /^\d+(?:[.,]\d+)?$/.test(text) ? `${text}%` : text;
};

const formatRestValue = (rest) => {
	const display = formatRestDisplay(rest);
	return display ? `R ${display}` : "";
};

const formatSetsReps = (sets, reps, setsLabel) => {
	const label = String(setsLabel ?? "").trim();
	const setsText = label || String(sets ?? "").trim();
	const repsText = String(reps ?? "").trim();
	if (setsText && repsText) return `${setsText}×${repsText}`;
	return setsText || repsText || "";
};

const isMinuteStrengthReps = (value) =>
	/\d\s*(?:'|′|’|min(?:ute|utes|uto|uti)?)(?![a-z])/i.test(String(value ?? ""));

export const isTimedStrengthReps = (value) =>
	/\d\s*(?:"|″|”|''|s(?:ec(?:ond(?:s|i)?)?)?\b)/i.test(String(value ?? "")) ||
	isMinuteStrengthReps(value);

const getStrengthDistanceUnit = (value) =>
	String(value ?? "")
		.trim()
		.match(/\d\s*(km|cm|mm|mi|m|yd|ft)\s*$/i)?.[1] || "";

export const isDistanceStrengthReps = (value) =>
	Boolean(getStrengthDistanceUnit(value));

// Quanti giri ha un superset, letti da «Giri totali» del builder
// (`superset_sets`, con `sets` come ripiego). Il campo e' testo libero e
// ammette un intervallo («3/4», «3-4»): si prende il massimo, cosi' la
// vista atleta mostra una riga per ogni giro possibile e chi ne fa quattro
// non ne perde uno. Prima `Number("3/4")` dava NaN e restava una serie sola.
export const getSupersetRoundCount = (exercise) => {
	const raw = exercise?.superset_sets ?? exercise?.sets;
	if (raw === null || raw === undefined) return 1;
	const numbers = String(raw)
		.match(/\d+(?:[.,]\d+)?/g)
		?.map((token) => Number(token.replace(",", ".")))
		.filter((value) => Number.isFinite(value) && value > 0);
	if (!numbers || numbers.length === 0) return 1;
	return Math.max(1, Math.floor(Math.max(...numbers)));
};

export const formatStrengthRepResult = (
	value,
	prescribedReps,
	repsLabel = "reps",
) => {
	const text = String(value ?? "").trim();
	if (!text) return "";
	if (isTimedStrengthReps(prescribedReps)) {
		if (isTimedStrengthReps(text)) return text;
		return isMinuteStrengthReps(prescribedReps) ? `${text}'` : `${text}s`;
	}
	const distanceUnit = getStrengthDistanceUnit(prescribedReps);
	if (distanceUnit) {
		return isDistanceStrengthReps(text) || isTimedStrengthReps(text)
			? text
			: `${text}${distanceUnit}`;
	}
	return isDistanceStrengthReps(text) ? text : `${text} ${repsLabel}`;
};

export const getStrengthPrescriptionRepsForSet = (exercise, setIndex) => {
	const rows = Array.isArray(exercise?.set_rows) ? exercise.set_rows : [];
	const targetIndex = Number(setIndex);
	if (!Number.isInteger(targetIndex) || targetIndex < 0) return exercise?.reps;

	let offset = 0;
	for (const row of rows) {
		const rowSets = Number(row?.sets);
		const setCount = Number.isFinite(rowSets) && rowSets > 0 ? rowSets : 1;
		if (targetIndex < offset + setCount) return row?.reps ?? exercise?.reps;
		offset += setCount;
	}

	return exercise?.reps ?? rows[rows.length - 1]?.reps;
};

const formatStrengthSetRow = (row) =>
	joinParts([
		formatSetsReps(row?.sets, row?.reps, row?.sets_label),
		formatLoadValue(row?.load_percentage),
		row?.rpe ? `RPE ${row.rpe}` : "",
		row?.rir ? `RIR ${row.rir}` : "",
		formatRestValue(row?.rest_seconds),
	]);

// Set rows are collapsed to at most two, then "+N": a reference row must stay
// on one line, and the full detail is one click away in the expanded view.
const formatSetRows = (exercise) => {
	const rows = Array.isArray(exercise?.set_rows) ? exercise.set_rows : [];
	const formatted = rows.map(formatStrengthSetRow).filter(Boolean);
	if (formatted.length === 0) {
		const legacy = formatStrengthSetRow({
			sets: exercise?.sets,
			reps: exercise?.reps,
			load_percentage: exercise?.load_percentage,
			rpe: exercise?.rpe,
			rir: exercise?.rir,
			rest_seconds: exercise?.rest_seconds,
		});
		return legacy;
	}
	if (formatted.length <= 2) return formatted.join(" / ");
	return `${formatted.slice(0, 2).join(" / ")} +${formatted.length - 2}`;
};

const formatSupersetPrescription = (exercise) => {
	const items = Array.isArray(exercise?.superset_exercises)
		? exercise.superset_exercises
		: [];
	const names = items
		.map((item) => String(item?.exercise_name || "").trim())
		.filter(Boolean);
	return joinParts([
		exercise?.superset_sets ? `${exercise.superset_sets} giri` : "",
		names.length > 0 ? names.join(" + ") : "",
		formatRestValue(exercise?.superset_rest_seconds),
	]);
};

const formatComplexPrescription = (exercise) => {
	const items = Array.isArray(exercise?.complex_exercises)
		? exercise.complex_exercises
		: [];
	const sequence = items
		.map((item) => {
			const name = String(item?.exercise_name || "").trim();
			const reps = String(item?.reps || "").trim();
			if (!name) return "";
			return reps ? `${reps} ${name}` : name;
		})
		.filter(Boolean)
		.join(" + ");
	return joinParts([sequence, formatSetRows(exercise)]);
};

// Movimenti di un EMOM (strength o conditioning), un elemento per round.
// Condiviso perché il conditioning EMOM tiene i movimenti in `emom_rounds`,
// non in `conditioning_exercises`.
const formatEmomRoundsSummary = (exercise) =>
	normalizeEmomRoundTemplates(exercise)
		.map((round) => {
			if (round?.input_mode === "library") {
				return (round.items || [])
					.map((item) => {
						const name = String(item?.exercise_name || "").trim();
						const reps = String(item?.reps || "").trim();
						if (!name) return "";
						return reps ? `${reps} ${name}` : name;
					})
					.filter(Boolean)
					.join(" + ");
			}
			return String(round?.content || "").trim();
		})
		.filter(Boolean);

const formatEmomPrescription = (exercise, t) => {
	const roundSummary = formatEmomRoundsSummary(exercise);
	return joinParts([
		formatEmomStructure(exercise, t),
		roundSummary.length > 0 ? roundSummary.join(" | ") : "",
	]);
};

const formatRunningPrescription = (exercise, t) => {
	const rows = Array.isArray(exercise?.interval_rows)
		? exercise.interval_rows
		: [];
	if (rows.length > 0) {
		const formatted = rows
			.map((row) => {
				const repetitions = Number(row?.repetitions);
				const prefix =
					Number.isFinite(repetitions) && repetitions > 1
						? `${repetitions}×`
						: "";
				const distance = String(row?.distance || "").trim();
				const pace = String(
					row?.target_pace || row?.target_pace_resolved || "",
				).trim();
				const recovery = String(row?.recovery_time || "").trim();
				return joinParts(
					[
						`${prefix}${distance}`,
						pace ? `@${pace}` : "",
						recovery ? `${rx("pbRxRec", t)} ${recovery}` : "",
					],
					" ",
				);
			})
			.filter(Boolean);
		if (formatted.length > 0) return formatted.join(" / ");
	}

	// Fartlek e progressivo sono una lista di tratti: «3 segmenti» diceva
	// quanti erano e taceva i ritmi, che sono la seduta. Ogni tratto esce come
	// durata @ ritmo; senza ritmo, il tipo di tratto (veloce / medio / lento).
	const segments = Array.isArray(exercise?.fartlek_segments)
		? exercise.fartlek_segments.filter(Boolean)
		: [];
	if (segments.length > 0) {
		const segmentTypeWord = {
			fast: rx("pbRxSegFast", t),
			medium: rx("pbRxSegMedium", t),
			slow: rx("pbRxSegSlow", t),
		};
		const formatted = segments
			.map((seg) => {
				const duration = String(seg?.duration || "").trim();
				const pace = String(seg?.pace || seg?.pace_resolved || "").trim();
				const rawType = String(seg?.segment_type || seg?.type || "").trim();
				const typeWord =
					rawType && rawType !== "work"
						? segmentTypeWord[normalizeFartlekSegmentType(rawType)] || ""
						: "";
				return joinParts([duration, pace ? `@${pace}` : typeWord], " ");
			})
			.filter(Boolean);
		const body = formatted.join(" / ");
		if (!body) {
			return joinParts([
				exercise?.fartlek_repeat ? `${exercise.fartlek_repeat}×` : "",
				`${segments.length} ${rx("pbRxSegments", t)}`,
			]);
		}
		return exercise?.fartlek_repeat
			? `${exercise.fartlek_repeat}× (${body})`
			: body;
	}

	const legacyIntervals = Number(exercise?.intervals);
	const distance = formatDistanceValue(exercise?.distance_meters);
	const pace = String(
		exercise?.target_pace || exercise?.target_pace_resolved || "",
	).trim();
	return joinParts([
		Number.isFinite(legacyIntervals) && legacyIntervals > 1 && distance
			? `${legacyIntervals}×${distance}`
			: distance,
		exercise?.duration_minutes ? `${exercise.duration_minutes}'` : "",
		pace ? `@${pace}` : "",
	]);
};

const formatSwimPrescription = (exercise, t) => {
	const sets = Array.isArray(exercise?.swim_sets) ? exercise.swim_sets : [];
	if (sets.length > 0) {
		const formatted = sets
			.map((set) => {
				const repetitions = Number(set?.repetitions);
				const prefix =
					Number.isFinite(repetitions) && repetitions > 1
						? `${repetitions}×`
						: "";
				const distance = String(set?.distance || "").trim();
				const target = String(set?.target_time || "").trim();
				const rest = String(set?.rest || "").trim();
				return joinParts(
					[
						`${prefix}${distance}`,
						target ? `@${target}` : "",
						rest ? `${rx("pbRxRec", t)} ${rest}` : "",
					],
					" ",
				);
			})
			.filter(Boolean);
		if (formatted.length > 0) return formatted.join(" / ");
	}
	return joinParts([
		formatDistanceValue(exercise?.distance_meters),
		exercise?.duration_minutes ? `${exercise.duration_minutes}'` : "",
	]);
};

const formatBikePrescription = (exercise, t) => {
	const intervals = Array.isArray(exercise?.bike_intervals)
		? exercise.bike_intervals
		: [];
	if (intervals.length > 0) {
		const formatted = intervals
			.map((interval) => {
				const repetitions = Number(interval?.repetitions);
				const prefix =
					Number.isFinite(repetitions) && repetitions > 1
						? `${repetitions}×`
						: "";
				const duration = String(interval?.duration || "").trim();
				const intensity = String(
					interval?.intensity || interval?.power || "",
				).trim();
				const recovery = String(interval?.recovery || "").trim();
				return joinParts(
					[
						`${prefix}${duration}`,
						intensity ? `@${intensity}` : "",
						recovery ? `${rx("pbRxRec", t)} ${recovery}` : "",
					],
					" ",
				);
			})
			.filter(Boolean);
		if (formatted.length > 0) return formatted.join(" / ");
	}
	return joinParts([
		formatDistanceValue(exercise?.distance_meters),
		exercise?.duration_minutes ? `${exercise.duration_minutes}'` : "",
		String(exercise?.bike_power_target || "").trim(),
	]);
};

const formatConditioningPrescription = (exercise, t) => {
	const conditioningType = String(exercise?.conditioning_type || "")
		.trim()
		.toLowerCase();

	const movements = (
		Array.isArray(exercise?.conditioning_exercises)
			? exercise.conditioning_exercises
			: []
	)
		.map((item) => {
			const name = String(item?.exercise_name || "").trim();
			const reps = String(item?.reps || "").trim();
			if (!name) return "";
			return reps ? `${reps} ${name}` : name;
		})
		.filter(Boolean)
		.join(" + ");

	const structure = [];
	// Il conditioning EMOM tiene i movimenti in `emom_rounds`, non in
	// `conditioning_exercises`: senza questo la riga espansa mostrava solo
	// l'intestazione (es. "4 round · ogni 1 minuto") e nessun movimento.
	let emomMovements = "";
	if (conditioningType === "amrap") {
		const cap = formatConditioningDuration(exercise?.time_cap_seconds, t);
		if (cap) structure.push(cap);
	} else if (conditioningType === "for_time") {
		if (exercise?.rounds) {
			structure.push(
				`${getForTimeRoundsDisplay(exercise.rounds)} ${rx("pbRxRound", t)}`,
			);
		}
		const cap = formatConditioningDuration(exercise?.time_cap_seconds, t);
		if (cap) structure.push(`${rx("pbRxCap", t)} ${cap}`);
	} else if (conditioningType === "emom") {
		// Stessa scrittura del ramo forza: "10 round · ogni 1:00".
		structure.push(...getEmomStructureParts(exercise, t));
		emomMovements = formatEmomRoundsSummary(exercise).join(" | ");
	} else if (conditioningType === "hiit") {
		if (exercise?.hiit_cycles)
			structure.push(`${exercise.hiit_cycles} ${rx("pbRxCycles", t)}`);
		const work = formatHiitIntervalShort(getHiitWorkSecondsPerRound(exercise));
		if (work) structure.push(`${work} ${rx("pbRxWork", t)}`);
		const off = getHiitOffLabel(exercise);
		if (off) structure.push(off);
	}

	const freeText = String(exercise?.conditioning_free_text || "")
		.replace(/\s+/g, " ")
		.trim();

	const resolvedMovements = movements || emomMovements;

	return joinParts([
		structure.join(" · "),
		resolvedMovements,
		resolvedMovements ? "" : freeText.slice(0, 80),
	]);
};

/**
 * Every strength set row of an exercise, one formatted string each.
 * Used by the reference panel's expanded row, where the "+N" truncation of
 * `formatExercisePrescription` is no longer wanted.
 *
 * @param {object} exercise program exercise
 * @returns {string[]} formatted rows (empty when the exercise has none)
 */
export const formatExerciseSetRowLines = (exercise) => {
	const rows = Array.isArray(exercise?.set_rows) ? exercise.set_rows : [];
	return rows.map(formatStrengthSetRow).filter(Boolean);
};

/**
 * One-line summary of an exercise's prescription, without its name.
 *
 * @param {object} exercise program exercise (any `exercise_type`)
 * @returns {string} compact summary, or "" when there is nothing to show
 */
/**
 * Riepilogo compatto di un esercizio di tiro, per ReferencePanel, matrice
 * progressioni e appunti. Mostra i numeri che il coach guarda per primi:
 * distanza, stringhe/ripetizioni, par time, colpi.
 */
export const formatShootingPrescription = (exercise, t) => {
	const shooting = exercise?.shooting || {};
	const parts = [];

	// Formato libero: non c'è volume né bersagli da riassumere, solo la durata.
	if (shooting.prescription_format === "free") {
		if (shooting.duration_minutes)
			parts.push(`${shooting.duration_minutes} min`);
		if (shooting.firearm_type) {
			const weapon = {
				handgun: rx("pbRxHandgun", t),
				rifle: rx("pbRxRifle", t),
				shotgun: rx("pbRxShotgun", t),
				pcc: rx("pbRxPcc", t),
			}[shooting.firearm_type];
			if (weapon) parts.push(weapon);
		}
		return parts.length ? parts.join(" · ") : rx("pbRxFree", t);
	}

	const focus = SHOOTING_FOCUS_OPTIONS.find(
		(f) => f.value === shooting.drill_focus,
	);
	if (focus) parts.push(typeof t === "function" ? t(focus.labelKey) : focus.label);

	if (shooting.firearm_type) {
		const weapon = {
			handgun: rx("pbRxHandgun", t),
			rifle: rx("pbRxRifle", t),
			shotgun: rx("pbRxShotgun", t),
			pcc: rx("pbRxPcc", t),
		}[shooting.firearm_type];
		if (weapon) parts.push(weapon);
	}

	if (shooting.mode === "dry_fire") {
		if (exercise.sets) parts.push(`${exercise.sets} ${rx("pbRxReps", t)}`);
		if (shooting.par_time_seconds)
			parts.push(`par ${shooting.par_time_seconds}s`);
	} else {
		// Il numero di serie è la lunghezza della lista quando c'è, il campo
		// numerico altrimenti; i colpi sono **per serie**, e lo si dice.
		const seriesList = Array.isArray(shooting.series) ? shooting.series : [];
		const seriesPlanned =
			seriesList.length || shooting.series_planned || shooting.strings_planned;
		if (seriesPlanned)
			parts.push(`${seriesPlanned} ${rx("pbRxSeries", t)}`);
		if (shooting.rounds_planned)
			parts.push(
				`${shooting.rounds_planned} ${rx(
					seriesPlanned > 1 ? "pbRxShotsPerSeries" : "pbRxShots",
					t,
				)}`,
			);
	}

	const targets = formatShootingTargets(shooting.targets, t);
	if (targets) parts.push(targets);
	if (shooting.target_setup) parts.push(shooting.target_setup);

	return parts.join(" · ").slice(0, 80);
};

export const formatExercisePrescription = (exercise, t) => {
	if (!exercise || typeof exercise !== "object") return "";

	try {
		const exerciseType = exercise.exercise_type || "strength";

		if (exerciseType === "strength") {
			switch (exercise.methodology) {
				case "superset":
					return formatSupersetPrescription(exercise);
				case "complex":
					return formatComplexPrescription(exercise);
				case "emom":
					return formatEmomPrescription(exercise, t);
				case "custom":
					return String(exercise.custom_workout_text || "")
						.replace(/\s+/g, " ")
						.trim()
						.slice(0, 80);
				default:
					return formatSetRows(exercise);
			}
		}

		if (exerciseType === "running")
			return formatRunningPrescription(exercise, t);
		if (exerciseType === "swim") return formatSwimPrescription(exercise, t);
		if (exerciseType === "bike" || exerciseType === "erg") {
			return formatBikePrescription(exercise, t);
		}
		if (exerciseType === "conditioning") {
			return formatConditioningPrescription(exercise, t);
		}
		if (isShootingExerciseType(exerciseType)) {
			return formatShootingPrescription(exercise, t);
		}

		return formatSetRows(exercise);
	} catch {
		// A malformed exercise must never break the reference panel.
		return "";
	}
};

/**
 * Display name of an exercise as shown in the reference panel: conditioning
 * gets its canonical "Conditioning - <type>" form, everything else uses the
 * stored name with a type-variant fallback.
 */
export const formatExerciseDisplayName = (exercise) => {
	if (!exercise || typeof exercise !== "object") return "";
	const exerciseType = exercise.exercise_type || "strength";
	if (exerciseType === "conditioning") {
		return formatConditioningDisplayName(
			exercise.exercise_name,
			exercise.conditioning_type,
		);
	}
	const name = String(exercise.exercise_name || "").trim();
	if (name) return name;
	if (exerciseType === "running") {
		return (
			RUNNING_TYPES.find((item) => item.value === exercise.running_type)
				?.label || "Corsa"
		);
	}
	if (exerciseType === "swim") {
		return (
			SWIM_TYPES.find((item) => item.value === exercise.swim_type)?.label ||
			"Nuoto"
		);
	}
	if (exerciseType === "bike") {
		return (
			BIKE_TYPES.find((item) => item.value === exercise.bike_type)?.label ||
			"Bici"
		);
	}
	if (exerciseType === "erg") {
		return getErgTypeLabel(exercise.bike_type) || "Erg";
	}
	return "Esercizio";
};

// --- Cross-week progression matching -----------------------------------------

const RUNTIME_ID_PATTERN = /-/;
// The Add-Exercise dialog mints `ex_<b36>_<b36>` for exercises not picked from
// the library. Same underscore prefix as a library id, but a second underscore
// a library id (`ex_<12 hex>`) never has.
const LEGACY_RUNTIME_ID_PATTERN = /^ex_[0-9a-z]+_[0-9a-z]+$/;

/**
 * Whether an id was produced at runtime (`createRuntimeId`'s `ex-<b36>-<b36>`,
 * or the dialog's `ex_<b36>_<b36>`) rather than being a stable library
 * reference (`ex_<hex>` — see `models/exercise.py`). Runtime ids are unique per
 * instance, so they can never match across weeks.
 */
const isRuntimeExerciseId = (id) => {
	const value = String(id || "");
	return (
		RUNTIME_ID_PATTERN.test(value) || LEGACY_RUNTIME_ID_PATTERN.test(value)
	);
};

const normalizeMatchName = (name) =>
	String(name || "")
		.trim()
		.toLowerCase()
		.replace(/\s+/g, " ");

/**
 * The two identities used to line an exercise up with "the same" exercise in
 * other weeks: the library id and the name. Either one is enough to match.
 *
 * - `idKey`: `source_exercise_id` comes first because
 *   `cloneExerciseAsIndependent` preserves it through every duplication while
 *   regenerating `exercise_id`, so a week duplicated five times still resolves
 *   to one row. It survives a rename. `null` for runtime ids.
 * - `nameKey`: the same movement picked once from the personal library and
 *   once from the global one, or typed by hand, carries different ids (or none)
 *   under the same name. Matching on the id alone split it into unrelated rows.
 *
 * Both `null` means no identity at all — the caller must then give the exercise
 * a row of its own rather than guessing.
 */
export const getExerciseMatchKeys = (exercise) => {
	const libraryId = exercise?.source_exercise_id || exercise?.exercise_id || "";
	const name = normalizeMatchName(formatExerciseDisplayName(exercise));
	return {
		idKey:
			libraryId && !isRuntimeExerciseId(libraryId) ? `id:${libraryId}` : null,
		nameKey: name ? `name:${name}` : null,
	};
};

const matchIdentities = (exercise) => {
	const { idKey, nameKey } = getExerciseMatchKeys(exercise);
	return [idKey, nameKey].filter(Boolean);
};

const COMPOSITE_METHODOLOGIES = new Set([
	"superset",
	"complex",
	"emom",
	"custom",
]);

// Stable library ids use `ex_<hex>` (no hyphen); runtime ids contain a hyphen.
const looksLikeStableLibraryId = (id) => {
	const value = String(id || "");
	return value !== "" && !isRuntimeExerciseId(value);
};

/**
 * Guards a program exercise from carrying the id of a DIFFERENT library
 * exercise, which would make its logged results contaminate that exercise's
 * performance profile / 1RM and exercise history (see
 * scripts/diagnose_profile_exercise_linking.py).
 *
 * Both id links are checked, because either one is enough to mis-route a result:
 * - `exercise_id`, the link the coach picked;
 * - `source_exercise_id`, which `cloneExerciseAsIndependent` carries through
 *   every week duplication. A row cloned from "Bench Press" and then edited into
 *   "Deadlift" keeps pointing at bench forever unless it is cleared here.
 *
 * Rules:
 * - Composite strength exercises (superset/complex/emom/custom) are not a single
 *   tracked lift, so any stable `exercise_id` they carry is dropped.
 * - Ids that resolve (via `libraryNameById`) to a DIFFERENT name are stale links
 *   and get decoupled. When an id is absent from the map we leave it untouched to
 *   avoid false positives on libraries the current user can't see.
 */
export const ensureExerciseIdIntegrity = (exercise, libraryNameById = null) => {
	if (!exercise || typeof exercise !== "object") return exercise;

	const exerciseType = exercise.exercise_type || "strength";
	const methodology = exercise.methodology || "standard";
	const isComposite =
		exerciseType === "strength" && COMPOSITE_METHODOLOGIES.has(methodology);
	const currentName = normalizeMatchName(
		exercise.exercise_name || formatExerciseDisplayName(exercise),
	);

	const namesAnotherMovement = (id) => {
		if (!(libraryNameById instanceof Map) || !libraryNameById.has(id)) {
			return false;
		}
		const libraryName = normalizeMatchName(libraryNameById.get(id));
		return Boolean(libraryName && currentName && libraryName !== currentName);
	};

	const patch = {};
	const id = exercise.exercise_id;
	if (
		looksLikeStableLibraryId(id) &&
		(isComposite || namesAnotherMovement(id))
	) {
		patch.exercise_id = createRuntimeId("ex");
		patch.source_exercise_id = null;
	}

	if (
		!("source_exercise_id" in patch) &&
		looksLikeStableLibraryId(exercise.source_exercise_id) &&
		namesAnotherMovement(exercise.source_exercise_id)
	) {
		patch.source_exercise_id = null;
	}

	return Object.keys(patch).length > 0 ? { ...exercise, ...patch } : exercise;
};

/**
 * Highest load percentage prescribed by an exercise, or `null` when the load is
 * not expressed as a percentage (absolute kg, "BW", conditioning, …).
 *
 * Only used to decide whether two cells of the progression matrix are
 * comparable: no percentage means no arrow, never a fabricated one.
 */
export const getExerciseLoadPercentage = (exercise) => {
	const values = [];

	const collect = (raw) => {
		// Decimal comma first: "72,5%" otherwise reads as 5%.
		const text = String(raw ?? "")
			.trim()
			.replace(/(\d),(\d)/g, "$1.$2");
		if (!text) return;
		// Bare number: the editor renders it with a "%" suffix, so read it as a
		// percentage here too.
		if (/^\d+(?:\.\d+)?$/.test(text)) {
			const bare = Number(text);
			if (Number.isFinite(bare)) values.push(bare);
			return;
		}
		// Bare range ("70-80", what saving makes of "70/80"): a percentage range
		// only when it climbs. A descending pair is the men/women kg convention
		// ("60/40"), and guessing there would invent a delta.
		if (/^\d+(?:\.\d+)?(?:\s*-\s*\d+(?:\.\d+)?)+$/.test(text)) {
			const bounds = text.split("-").map((part) => Number(part.trim()));
			const climbs = bounds.every(
				(bound, index) => index === 0 || bound >= bounds[index - 1],
			);
			if (climbs && bounds.every(Number.isFinite)) values.push(...bounds);
			return;
		}
		extractPercentageNumbers(text).forEach((match) => {
			const parsed = Number(String(match).replace("%", "").trim());
			if (Number.isFinite(parsed)) values.push(parsed);
		});
	};

	const rows = Array.isArray(exercise?.set_rows) ? exercise.set_rows : [];
	rows.forEach((row) => collect(row?.load_percentage));
	if (rows.length === 0) collect(exercise?.load_percentage);

	return values.length > 0 ? Math.max(...values) : null;
};

/**
 * Percentage load of every exercise in a week, ready for
 * `findReferenceLoad`. Exercises without a percentage stay in, with
 * `load: null`: a match on them must answer "not comparable", not fall through
 * to the same movement on another day.
 */
export const buildWeekLoadIndex = (week) => {
	const entries = [];
	(week?.days || []).forEach((day, dayIndex) => {
		getNormalizedDayExercises(day).forEach((exercise) => {
			const identities = matchIdentities(exercise);
			if (identities.length === 0) return;
			entries.push({
				dayIndex,
				identities,
				load: getExerciseLoadPercentage(exercise),
			});
		});
	});
	return entries;
};

/**
 * The reference load for `exercise` on `dayIndex`: the same movement on the
 * same day first, then anywhere in the reference week (programs move a lift
 * between days from one week to the next). Within each, the library id beats
 * the name.
 *
 * @returns {number|null|undefined} `undefined` when the movement is not in the
 *   reference week, `null` when it is but its load is not a percentage
 */
export const findReferenceLoad = (index, exercise, dayIndex) => {
	if (!Array.isArray(index) || index.length === 0) return undefined;
	const identities = matchIdentities(exercise);
	const sameDay = index.filter((entry) => entry.dayIndex === dayIndex);
	for (const pool of [sameDay, index]) {
		for (const identity of identities) {
			const entry = pool.find((item) => item.identities.includes(identity));
			if (entry) return entry.load;
		}
	}
	return undefined;
};

/**
 * Default week/day labels are generated by the builder and *persisted* as
 * "Settimana N" / "Giorno N". Translating them in render only is not enough,
 * and re-writing stored data would clobber coach-authored titles. So we match
 * the generated shape and localize just that, leaving any custom title alone.
 *
 * @param {string} label stored label
 * @param {Function} t translator from `useLanguage()`
 * @param {number} fallbackIndex zero-based index used when no label is stored
 * @returns {string} label to display
 */
export const localizeWeekLabel = (label, t, fallbackIndex = 0) => {
	const raw = String(label || "").trim();
	const match = raw.match(/^(?:settimana|week)\s+(\d+)$/i);
	if (match) return `${t("week")} ${match[1]}`;
	if (raw) return raw;
	return `${t("week")} ${fallbackIndex + 1}`;
};

/**
 * Same contract as `localizeWeekLabel`, for the day titles the builder
 * generates as "Giorno N".
 */
export const localizeDayLabel = (label, t, fallbackIndex = 0) => {
	const raw = String(label || "").trim();
	const match = raw.match(/^(?:giorno|day)\s+(\d+)$/i);
	if (match) return `${t("day")} ${match[1]}`;
	if (raw) return raw;
	return `${t("day")} ${fallbackIndex + 1}`;
};

/**
 * Builds the progression matrix: for each day position, the exercises that
 * appear there across all weeks, with one cell per week.
 *
 * Grouping is by day *position* (day 1, day 2, …) because that is how a program
 * is written and read. An exercise moved to another day in a later week
 * therefore shows up under that other day — accurate, if not merged.
 *
 * @param {Array} weeks program weeks
 * @returns {Array} day groups, each with `rows` holding one cell per week
 */
export const matchExercisesAcrossWeeks = (weeks) => {
	const weekList = Array.isArray(weeks) ? weeks : [];
	if (weekList.length === 0) return [];

	const dayCount = weekList.reduce(
		(max, week) => Math.max(max, (week?.days || []).length),
		0,
	);

	const groups = [];

	for (let dayIndex = 0; dayIndex < dayCount; dayIndex += 1) {
		const rows = [];
		// identity (id or name) -> rows carrying it, in creation order
		const rowsByIdentity = new Map();
		let label = "";

		weekList.forEach((week, weekIndex) => {
			const day = (week?.days || [])[dayIndex];
			if (!day) return;
			if (!label) label = String(day.title || "").trim();

			getNormalizedDayExercises(day).forEach((exercise) => {
				const identities = matchIdentities(exercise);
				// A row already filled this week is taken: the same exercise twice
				// in one day keeps both, each on its own row.
				const isFree = (candidate) => !candidate.cells[weekIndex];
				let row = null;
				for (const identity of identities) {
					row = (rowsByIdentity.get(identity) || []).find(isFree) || null;
					if (row) break;
				}
				if (!row) {
					row = {
						key: `${identities[0] || "unmatched"}#${rows.length}`,
						name: formatExerciseDisplayName(exercise),
						exerciseType: exercise?.exercise_type || "strength",
						cells: {},
					};
					rows.push(row);
				}
				row.cells[weekIndex] = { weekIndex, dayIndex, exercise };
				identities.forEach((identity) => {
					const known = rowsByIdentity.get(identity) || [];
					if (!known.includes(row)) known.push(row);
					rowsByIdentity.set(identity, known);
				});
			});
		});

		if (rows.length === 0) continue;

		groups.push({
			dayIndex,
			label: label || String(dayIndex + 1),
			rows: rows.map((row) => {
				const cells = [];
				for (let weekIndex = 0; weekIndex < weekList.length; weekIndex += 1) {
					cells.push(row.cells[weekIndex] || null);
				}
				return {
					key: row.key,
					name: row.name,
					exerciseType: row.exerciseType,
					cells,
				};
			}),
		});
	}

	return groups;
};
